import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE, apiFetch, AuthRequiredError } from "../lib/api";
import { useKeyboardHeight } from "../hooks/useKeyboardHeight";
import SharedFolderSidebar from "../components/SharedFolderSidebar";
import { Thumbnail } from "../components/Thumbnail";
import { VerificationRequiredModal } from "../components/VerificationRequiredModal";
import { ArtifactPanel, extractArtifactTitle } from "../components/ArtifactPanel";
import {
  ChevronDown,
  ChevronRight,
  Check,
  X,
  ExternalLink,
  Copy,
  CheckCheck,
  MessageSquare,
  Plus,
  Trash2,
  Pencil,
  PanelLeftClose,
  PanelLeftOpen,
  FolderOpen,
  ArrowLeft,
  Lock,
  FileOutput,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { TranscriptIcon } from "../components/icons/TranscriptIcon";
import { GlobeSearchIcon, NeuralIcon, AIChipIcon } from "../components/icons/GradientIcons";
import { audioManager } from "../services/audioManager";

interface Episode {
  id: string;
  title: string;
  podcast_name?: string;
}

interface FolderItem {
  id: string;
  name: string;
  episode_count: number;
  episodes?: Episode[];
  sort_order?: number;
  parent_id?: string | null;
  folder_type?: string;
}

interface ChatSession {
  id: string;
  title: string;
  updated_at: string;
}

interface Citation {
  number: number;  // The citation number [1], [2], etc.
  item_id: string;
  episode_title: string;
  start_ms: number;
  timestamp: string;
  speaker: string;
  snippet: string;
}

interface WebCitation {
  number?: number;  // [W1], [W2], etc.
  artifact_id: string;
  url?: string;
  title?: string;
  domain?: string;
  date?: string;
  evidence_snippet?: string;
  type: 'web_search' | 'web_page' | 'x_search';
  // R5: Full content for in-app reading
  full_content?: string;
  source_urls?: { url: string; title: string; domain: string }[];
}

interface Message {
  role: string;
  content: string;
  citations?: Citation[];
  web_citations?: WebCitation[];
  units_consumed?: number;
  balance_after?: number;
  research_mode?: boolean;
}

// Tool call status for streaming research mode
interface ActiveTool {
  tool: string;
  query?: string;
  icon: string;
  label: string;
  status: 'running' | 'done' | 'error';
  summary?: string;
}

interface ResearchBalance {
  can_research: boolean;
  balance: number;
  beta_access: boolean;
  is_byok: boolean;
  tier: string;
}

interface ChatUsage {
  chat: {
    used: number;
    limit: number | null;  // null = unlimited
    is_unlimited: boolean;
  };
  research: {
    used: number;
    balance: number;
    total: number;
    has_access: boolean;
    is_unlimited: boolean;
  };
}

interface CitationContext {
  item_id: string;
  title: string;
  podcast_name: string;
  thumbnail_url: string;
  video_id: string;
  segments: Array<{
    speaker: string;
    text: string;
    start_ms: number;
    timestamp: string;
    is_cited: boolean;
  }>;
  cited_ms: number;
}

// Split grouped citations [1, 2, 3] into [1][2][3] for individual clickability
function splitGroupedCitations(text: string): string {
  return text.replace(
    /\[([\dW,\s]+)\]/g,
    (match, inner) => {
      const parts = inner.split(/,\s*/);
      // Check if these look like citation numbers (not random text in brackets)
      const allCitations = parts.every((p: string) => /^W?\d+$/.test(p.trim()));
      if (!allCitations || parts.length === 1) return match; // Don't split single or non-citation brackets
      return parts.map((p: string) => `[${p.trim()}]`).join('');
    }
  );
}

function parseMarkdown(
  text: string,
  citations?: Citation[],
  onCitationClick?: (c: Citation) => void,
  webCitations?: WebCitation[],
  onWebCitationClick?: (wc: WebCitation) => void,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Match: **bold**, [1] transcript citation, [W1] web citation
  const regex = /\*\*(.+?)\*\*|\[W(\d+)\]|\[(\d+)\]/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  // Build lookup map from citation number to citation object
  const citationMap = new Map<number, Citation>();
  citations?.forEach(c => citationMap.set(c.number, c));
  
  // Build lookup map for web citations
  const webCitationMap = new Map<number, WebCitation>();
  webCitations?.forEach(wc => {
    if (wc.number != null) webCitationMap.set(wc.number, wc);
  });

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      // Bold text
      parts.push(
        <strong key={key++} className="font-semibold">
          {match[1]}
        </strong>
      );
    } else if (match[2] !== undefined) {
      // Web citation [W1], [W2]
      const num = parseInt(match[2], 10);
      const webCitation = webCitationMap.get(num);
      if (webCitation && onWebCitationClick) {
        parts.push(
          <button
            key={key++}
            type="button"
            className="inline-flex items-center gap-0.5 text-emerald-600 font-medium cursor-pointer hover:underline hover:text-emerald-800 text-[11px] align-super"
            title={webCitation.title || webCitation.domain || webCitation.url || "Web source"}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onWebCitationClick(webCitation); }}
          >
            <span className="text-[9px]">🌐</span>W{match[2]}
          </button>
        );
      } else if (webCitation?.url) {
        // Fallback to link if no click handler
        parts.push(
          <a
            key={key++}
            href={webCitation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-emerald-600 font-medium cursor-pointer hover:underline hover:text-emerald-800 text-[11px] align-super"
            title={webCitation.title || webCitation.domain || webCitation.url}
          >
            <span className="text-[9px]">🌐</span>W{match[2]}
          </a>
        );
      } else {
        parts.push(<sup key={key++} className="ml-0.5 text-gray-400">[W{match[2]}]</sup>);
      }
    } else if (match[3] !== undefined) {
      // Transcript citation [1], [2]
      const num = parseInt(match[3], 10);
      const citation = citationMap.get(num);
      if (citation && onCitationClick) {
        parts.push(
          <button
            key={key++}
            type="button"
            className="inline text-blue-600 font-semibold cursor-pointer hover:underline hover:text-blue-800 align-super text-[11px]"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCitationClick(citation); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onCitationClick(citation); }}
            title={`${citation.episode_title} @ ${citation.timestamp}`}
          >
            [{match[3]}]
          </button>
        );
      } else {
        parts.push(<sup key={key++} className="ml-0.5 text-gray-400">[{match[3]}]</sup>);
      }
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

interface LLMModel {
  provider: string;
  model: string;
  label: string;
  available: boolean;
  requires_byok: boolean;
  default: boolean;
  locked_message: string | null;
}

// Model descriptions for dropdown (use partial match for flexibility)
const MODEL_DESCRIPTIONS: Record<string, string> = {
  "claude-sonnet-4": "Best for analysis",
  "claude-opus-4": "Most capable",
  "claude-haiku": "Fast & efficient",
  "gpt-4o-mini": "Fast & affordable",
  "gpt-4o": "Most capable",
  "gpt-4-turbo": "Previous flagship",
  "gemini-2.5-flash": "Fastest",
  "gemini-2.0-flash": "Balanced",
  "gemini-1.5-pro": "Most capable",
};

// Helper to find description by partial model match
function getModelDescription(model: string): string | undefined {
  // Try exact match first
  if (MODEL_DESCRIPTIONS[model]) return MODEL_DESCRIPTIONS[model];
  // Try partial match
  for (const [key, desc] of Object.entries(MODEL_DESCRIPTIONS)) {
    if (model.includes(key)) return desc;
  }
  return undefined;
}

// Fallback models if API fails
const FALLBACK_MODELS: LLMModel[] = [
  { provider: "anthropic", model: "claude-sonnet-4-20250514", label: "Claude Sonnet 4", available: true, requires_byok: false, default: true, locked_message: null },
  { provider: "openai", model: "gpt-4o-mini", label: "GPT-4o Mini", available: true, requires_byok: false, default: false, locked_message: null },
  { provider: "google", model: "gemini-2.5-flash", label: "Gemini 2.5 Flash", available: true, requires_byok: false, default: false, locked_message: null },
];

function formatMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Find the sentence in segmentText that best matches the LLM's claim.
 * Returns the start and end character indices within segmentText.
 */
function findBestMatchingSentence(
  segmentText: string,
  llmClaim: string
): { start: number; end: number } | null {
  if (!segmentText || !llmClaim) return null;

  // Split segment into sentences (rough but effective)
  const sentences: { text: string; start: number; end: number }[] = [];
  const sentenceRegex = /[^.!?]+[.!?]+/g;
  let match;
  while ((match = sentenceRegex.exec(segmentText)) !== null) {
    sentences.push({
      text: match[0].trim(),
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  // If no sentence breaks found, treat entire text as one sentence
  if (sentences.length === 0) {
    sentences.push({ text: segmentText, start: 0, end: segmentText.length });
  }

  // Extract meaningful words from LLM claim (4+ chars, lowercased)
  const claimWords = new Set(
    llmClaim
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .split(/\s+/)
      .filter((w) => w.length >= 4)
  );

  if (claimWords.size === 0) return null;

  // Score each sentence by word overlap with the LLM claim
  let bestScore = 0;
  let bestSentence: { text: string; start: number; end: number } | null = null;

  for (const sentence of sentences) {
    const sentenceWords = new Set(
      sentence.text
        .toLowerCase()
        .replace(/[^\w\s]/g, "")
        .split(/\s+/)
        .filter((w) => w.length >= 4)
    );

    // Count overlapping words
    let overlap = 0;
    for (const word of claimWords) {
      if (sentenceWords.has(word)) overlap++;
    }

    // Normalize by claim size (what fraction of claim words appear in this sentence)
    const score = overlap / claimWords.size;

    if (score > bestScore) {
      bestScore = score;
      bestSentence = sentence;
    }
  }

  // Only highlight if at least 20% of claim words match
  if (bestScore < 0.2 || !bestSentence) return null;

  return { start: bestSentence.start, end: bestSentence.end };
}

// Issue 1: Source Preview Card for web citations
const SourcePreviewCard = ({ citation }: { citation: WebCitation }) => {
  const inner = (
    <div className="flex items-start gap-3">
      {citation.domain && (
        <img
          src={`https://www.google.com/s2/favicons?domain=${citation.domain}&sz=32`}
          alt=""
          className="w-5 h-5 mt-0.5 rounded shrink-0"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 text-sm leading-snug line-clamp-2">
          {citation.title || 'Untitled source'}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {citation.domain || 'Unknown domain'}
          {citation.date ? ` · ${citation.date}` : ''}
        </p>
      </div>
      <ExternalLink className="h-3.5 w-3.5 text-gray-300 shrink-0 mt-0.5" />
    </div>
  );

  const cardClass = 'block bg-white border border-gray-200 rounded-lg p-4 mb-4 hover:border-emerald-400 hover:shadow-sm transition-all';

  if (citation.url) {
    return (
      <a href={citation.url} target="_blank" rel="noopener noreferrer" className={cardClass}>
        {inner}
      </a>
    );
  }

  return <div className={cardClass}>{inner}</div>;
};

// Issue 3: Normalize segments helper for RSS/clip items
const normalizeSegments = (segments: any[] = []) =>
  segments.map((seg, index) => ({
    id: seg.id ?? `${seg.start_ms ?? 0}-${index}`,
    speaker: seg.speaker ?? 'Speaker',
    text: seg.text ?? '',
    start_ms: typeof seg.start_ms === 'number' ? seg.start_ms : 0,
    end_ms: typeof seg.end_ms === 'number' ? seg.end_ms : undefined,
    timestamp: seg.timestamp,
    is_cited: !!seg.is_cited,
  }));

const COLLAPSED_WIDTH = 48;

export default function ChatPage() {
  const { token, loading: authLoading, isSessionExpired } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const itemIdFromUrl = searchParams.get("item");
  const itemIdsFromUrl = searchParams.get("items"); // comma-separated multi-item
  
  const [query, setQuery] = useState("");
  
  // Single item context (from ?item= URL param)
  const [singleItemContext, setSingleItemContext] = useState<{ 
    id: string; 
    title: string; 
    videoId?: string;
    audioAvailable?: boolean;
  } | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingEpisodes, setLoadingEpisodes] = useState<Set<string>>(new Set());
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [archivedSessions, setArchivedSessions] = useState<ChatSession[]>([]);
  const [archivedCollapsed, setArchivedCollapsed] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
    // Restore last session from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tempo_chat_session_id');
    }
    return null;
  });
  const [currentSessionTitle, setCurrentSessionTitle] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [sessionsCollapsed, setSessionsCollapsed] = useState(false);
  const [foldersCollapsed, setFoldersCollapsed] = useState(false);
  const [llmModels, setLlmModels] = useState<LLMModel[]>(FALLBACK_MODELS);
  const [selectedModel, setSelectedModel] = useState("claude-sonnet-4-20250514");
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [researchMode, setResearchMode] = useState(false);  // Default OFF - user enables when needed
  const [researchBalance, setResearchBalance] = useState<ResearchBalance | null>(null);
  const [chatUsage, setChatUsage] = useState<ChatUsage | null>(null);
  
  // Speaker Intelligence state
  const [speakers, setSpeakers] = useState<{ id: string; canonical_name: string; profile_status: string; total_speaking_ms: number; episode_count: number; confidence_score: number }[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<string | null>(null);
  const [speakerDropdownOpen, setSpeakerDropdownOpen] = useState(false);
  const speakerDropdownRef = useRef<HTMLDivElement>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedChat, setCopiedChat] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  
  // Verification modal
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationLimitType, setVerificationLimitType] = useState<'chat' | 'research'>('chat');
  const [verificationLimits, setVerificationLimits] = useState<{ limit: number; bonus: number } | null>(null);

  // Research mode streaming - active tool calls
  const [activeTools, setActiveTools] = useState<ActiveTool[]>([]);

  // Left sidebar
  const [sidebarPinned, setSidebarPinned] = useState(true);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(320);
  const [isResizingLeft, setIsResizingLeft] = useState(false);

  // Right panel
  const [rightPanelMode, setRightPanelMode] = useState<"closed" | "transcripts" | "citation" | "artifact" | "web-citation">("closed");
  const [webCitationPanel, setWebCitationPanel] = useState<WebCitation | null>(null);
  // Web proxy reader state
  const [webProxyContent, setWebProxyContent] = useState<string | null>(null);
  const [webProxyStatus, setWebProxyStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [webProxyTitle, setWebProxyTitle] = useState<string | null>(null);
  const [webProxyError, setWebProxyError] = useState<string | null>(null);
  const latestWebFetchRef = useRef<string | null>(null);
  const [citationPanel, setCitationPanel] = useState<CitationContext | null>(null);
  const [citationLoading, setCitationLoading] = useState(false);
  const [_citedSnippet, setCitedSnippet] = useState<string | null>(null);
  const [citedClaimText, setCitedClaimText] = useState<string>('');
  
  // Artifact panel (Claude-style document view)
  const [artifactContent, setArtifactContent] = useState<string | null>(null);
  const [artifactTitle, setArtifactTitle] = useState<string>("Report");
  const [artifactFullscreen, setArtifactFullscreen] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(480);
  const [isResizingRight, setIsResizingRight] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const foldersSetBySession = useRef(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const citedSegmentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const userScrolledUp = useRef(false);
  
  // iOS keyboard height tracking
  const { keyboardHeight: _keyboardHeight } = useKeyboardHeight();
  const sidebarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Right panel transcript browsing
  const [rightExpandedFolders, setRightExpandedFolders] = useState<Set<string>>(new Set());
  const [rightLoadingFolders, setRightLoadingFolders] = useState<Set<string>>(new Set());
  const [viewingTranscript, setViewingTranscript] = useState<{
    id: string;
    title: string;
    segments: Array<{ speaker: string; text: string; start_ms: number; end_ms: number }>;
    thumbnail_url?: string;
  } | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  
  // Standalone items: selected items not found in any loaded folder.episodes
  const [standaloneItems, setStandaloneItems] = useState<Array<{ id: string; title: string; folder_id?: string }>>([]);

  // Audio player state (for transcript panel)
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioCurrentTimeMs, setAudioCurrentTimeMs] = useState(0);
  const [audioVideoId, setAudioVideoId] = useState<string | null>(null);
  const [audioDirectUrl, setAudioDirectUrl] = useState<string | null>(null);  // For RSS redirect streams
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [pendingSeekMs, setPendingSeekMs] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastAutoScrollMs = useRef(0);  // Bug 3: karaoke auto-scroll tracking
  
  // Reliable seek function - handles both loaded and loading states
  const seekAudioTo = useCallback((timeMs: number) => {
    if (!audioRef.current) return;
    const timeSec = timeMs / 1000;
    if (audioRef.current.readyState >= 1) {
      // Metadata loaded, seek immediately
      audioRef.current.currentTime = timeSec;
    } else {
      // Wait for metadata, then seek
      setPendingSeekMs(timeMs);
    }
  }, []);
  
  // Handle pending seek when audio metadata loads
  useEffect(() => {
    if (pendingSeekMs !== null && audioRef.current && audioRef.current.readyState >= 1) {
      audioRef.current.currentTime = pendingSeekMs / 1000;
      setPendingSeekMs(null);
    }
  }, [pendingSeekMs, audioDuration]); // audioDuration changes when metadata loads

  const sidebarExpanded = sidebarPinned || sidebarHovered;
  const rightPanelOpen = rightPanelMode !== "closed";

  // ── Scoped overflow fix: prevent html/body scroll on chat page ──
  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    };
  }, []);

  // Bug 3: Auto-scroll transcript panel to follow audio playback (karaoke scroll)
  useEffect(() => {
    if (!audioPlaying || !audioAvailable || !viewingTranscript) return;
    // Only scroll every ~2 seconds to avoid jitter
    if (audioCurrentTimeMs - lastAutoScrollMs.current < 2000) return;
    lastAutoScrollMs.current = audioCurrentTimeMs;
    
    const container = document.querySelector('[data-panel="transcripts"]')?.parentElement;
    const activeEl = document.querySelector('[data-panel="transcripts"] [data-start-ms].border');
    if (container && activeEl) {
      const containerRect = container.getBoundingClientRect();
      const elRect = activeEl.getBoundingClientRect();
      if (elRect.bottom > containerRect.bottom || elRect.top < containerRect.top) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [audioCurrentTimeMs, audioPlaying, audioAvailable, viewingTranscript]);

  // ── Data fetching ──
  // Session expired banner — shown when a previously-authenticated session has expired
  const SessionExpiredBanner = () => (
    <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
      <div className="flex items-start gap-3">
        <svg className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">Session Expired</p>
          <p className="text-sm text-amber-700 mt-1">
            Your session has expired. Please log in again to access your chats.
          </p>
        </div>
        <button
          onClick={() => navigate("/login")}
          className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
        >
          Log in
        </button>
      </div>
    </div>
  );

  useEffect(() => {
    if (authLoading) return;
    if (!token) return;

    const fetchData = async () => {
      try {
        const [foldersData, sessionsData, modelsData, researchBalanceData, usageData] = await Promise.all([
          apiFetch<{ folders: FolderItem[] }>("/chat/folders"),
          apiFetch<ChatSession[]>("/chat/sessions"),
          apiFetch<LLMModel[]>("/chat/models"),
          apiFetch<ResearchBalance>("/chat/research-balance"),
          apiFetch<ChatUsage>("/chat/usage"),
        ]);

        setFolders(foldersData.folders || []);
        // No folders selected by default - user must choose context
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);

        // Fetch archived sessions separately
        try {
          const archivedData = await apiFetch<ChatSession[]>("/chat/sessions/archived");
          setArchivedSessions(archivedData || []);
        } catch (e) {
          if (e instanceof AuthRequiredError) throw e;
          console.error("Failed to fetch archived sessions:", e);
        }

        // Load available models
        setLlmModels(modelsData);
        // Set default model
        const defaultModel = modelsData.find((m) => m.default && m.available);
        if (defaultModel) {
          setSelectedModel(defaultModel.model);
        }

        // Load research balance
        setResearchBalance(researchBalanceData);

        // Load chat/research usage for display
        setChatUsage(usageData);
      } catch (err) {
        if (err instanceof AuthRequiredError) {
          // apiFetch already cleared the token; AuthContext sets isSessionExpired
          return;
        }
        console.error("Failed to fetch data:", err);
      } finally {
        setLoadingFolders(false);
      }
    };
    fetchData();
  }, [authLoading, token, itemIdFromUrl]);

  // Fetch speakers for Persona mode (globally available, not folder-specific)
  useEffect(() => {
    if (!token) return;
    
    fetch(`${API_BASE}/chat/speakers`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setSpeakers(data.speakers || []))
      .catch(() => setSpeakers([]));
  }, [token]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/chat/folders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setFolders(data.folders || []);
    } catch (err) {
      console.error("Failed to fetch folders:", err);
    }
  }, [token]);

  // Load single item context from URL param (?item=xxx)
  useEffect(() => {
    if (!itemIdFromUrl || !token) return;
    
    const loadItem = async () => {
      try {
        const res = await fetch(`${API_BASE}/library/items/${itemIdFromUrl}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const item = data.item || data;
          
          // Check audio availability
          let audioAvailable = false;
          const videoId = item.video_id;
          if (videoId) {
            try {
              const audioRes = await fetch(`${API_BASE}/audio/${encodeURIComponent(videoId)}/info`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (audioRes.ok) {
                const audioData = await audioRes.json();
                audioAvailable = audioData.available && audioData.playback_allowed !== false;
                if (audioAvailable) {
                  // Set duration from backend metadata (while audio loads)
                  if (audioData.duration_seconds) {
                    setAudioDuration(audioData.duration_seconds * 1000);
                  }
                  // Preload audio (pass direct URL for RSS streams)
                  const directUrl = audioData.stream_type === 'redirect' ? audioData.playback_url : undefined;
                  audioManager.preloadAudio(videoId, directUrl);
                  // Store direct URL for RSS streams (Bug 1 fix)
                  setAudioDirectUrl(directUrl || null);
                }
              }
            } catch (audioErr) {
              console.error("Failed to check audio:", audioErr);
            }
          }
          
          setSingleItemContext({ 
            id: item.id, 
            title: item.title,
            videoId,
            audioAvailable,
          });
          // Clear folder selection when using single item
          setSelectedFolders(new Set());
          setSelectedItems(new Set([item.id]));
          // Start a new session for this item
          setCurrentSessionId(null);
          setMessages([]);
          
          // Issue 3: Load transcript segments with normalization and fallback for RSS/clip items
          let segments = item.transcript_json ? JSON.parse(item.transcript_json) : [];
          segments = normalizeSegments(segments);
          
          if (segments.length === 0) {
            try {
              const segData = await apiFetch<{ segments: any[] }>(
                `/chat/citation/${item.id}?start_ms=0`
              );
              if (segData.segments && segData.segments.length > 0) {
                segments = normalizeSegments(segData.segments);
              }
            } catch (segErr) {
              console.warn('Fallback segment fetch failed:', segErr);
            }
          }
          
          // Store transcript data for when user wants to view it
          setViewingTranscript({ id: item.id, title: item.title, segments });
          // Don't auto-open transcript panel - let user start chatting immediately
          // They can click the transcript button if they want to see it
          // setRightPanelMode("transcripts");
          
          // Set audio state (but don't open panel)
          if (audioAvailable && videoId) {
            setAudioVideoId(videoId);
            setAudioAvailable(true);
          }
        }
      } catch (err) {
        console.error("Failed to load item:", err);
      }
    };
    loadItem();
  }, [itemIdFromUrl, token]);

  // Load multiple items from URL param (?items=id1,id2,id3)
  useEffect(() => {
    console.log('[items] effect ran. itemIdsFromUrl:', !!itemIdsFromUrl, 'token:', !!token);
    if (!itemIdsFromUrl || !token) return;
    let cancelled = false;
    const ids = itemIdsFromUrl.split(',').filter(Boolean);
    if (ids.length === 0) return;

    const loadItems = async () => {
      // Option A: use navigation state if available (instant — no API calls)
      const navState = location.state as { items?: Array<{ id: string; title: string }> } | null;
      const stateItems = navState?.items?.filter(i => ids.includes(i.id));

      if (stateItems && stateItems.length === ids.length) {
        // All items present in navigation state — use directly
        if (cancelled) return;
        console.log('[items] loaded from nav state:', stateItems.length);
        setSelectedItems(new Set(stateItems.map(i => i.id)));
        setStandaloneItems(stateItems);
        setSelectedFolders(new Set());
        setCurrentSessionId(null);
        setMessages([]);
        foldersSetBySession.current = false;
        return;
      }

      // Option B: fall back to batch API call (page refresh / direct URL)
      try {
        const data = await apiFetch<{ items: Array<{ id: string; title: string }> }>(
          '/library/items/batch',
          { method: 'POST', body: JSON.stringify({ item_ids: ids }) }
        );
        if (cancelled) return;
        const loaded = data.items || [];
        console.log('[items] loaded from batch API:', loaded.length);
        setSelectedItems(new Set(loaded.map(i => i.id)));
        setStandaloneItems(loaded);
        setSelectedFolders(new Set());
        setCurrentSessionId(null);
        setMessages([]);
        foldersSetBySession.current = false;
      } catch (e: any) {
        console.log('[items] batch fetch failed:', e?.message);
      }
    };

    loadItems();
    return () => { 
      console.log('[items] CLEANUP — cancelled set to true');
      cancelled = true; 
    };
  }, [itemIdsFromUrl, token, location]);

  // Smart auto-scroll: only scroll if user hasn't scrolled up manually
  useEffect(() => {
    if (!userScrolledUp.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Reset scroll flag when user sends a new message
  useEffect(() => {
    if (loading) {
      userScrolledUp.current = false;
    }
  }, [loading]);

  // Detect when user scrolls up manually
  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    userScrolledUp.current = !isNearBottom;
  };

  // Ref for citation panel scroll container
  const citationScrollRef = useRef<HTMLDivElement>(null);
  
  // Issue 2: Scroll to cited segment when citation panel loads
  useEffect(() => {
    console.log('[CITATION-SCROLL] useEffect triggered. citationPanel:', !!citationPanel, 'citationLoading:', citationLoading);
    if (!citationPanel || citationLoading) return;
    
    console.log('[CITATION-SCROLL] useEffect fired. citationPanel exists, not loading.');

    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const MAX_ATTEMPTS = 15;
    const INTERVAL_MS = 150;

    const tryScroll = () => {
      attempts += 1;
      
      // Find the VISIBLE citation panel container (the one with actual dimensions)
      // This avoids ref conflicts when both desktop and mobile panels render
      const allContainers = document.querySelectorAll('[data-panel="citation"]');
      let container: HTMLElement | null = null;
      allContainers.forEach(el => {
        if ((el as HTMLElement).offsetHeight > 0) {
          container = el as HTMLElement;
        }
      });
      
      // Find the cited segment within that container (uses data attribute, not styling class)
      const cited = container?.querySelector('[data-cited]') as HTMLElement | null;
      
      console.log(`[CITATION-SCROLL] Attempt ${attempts}/${MAX_ATTEMPTS}. container:`, 
        container ? `${container.offsetHeight}px` : 'null', 
        'cited:', cited ? cited.dataset?.startMs : 'null');

      if (cited && container) {
        // Wait until container has meaningful scroll height (all segments rendered)
        if (container.scrollHeight <= container.clientHeight && attempts < MAX_ATTEMPTS) {
          console.log(`[CITATION-SCROLL] Container not scrollable yet. scrollHeight=${container.scrollHeight}, clientHeight=${container.clientHeight}`);
          timeoutId = setTimeout(tryScroll, INTERVAL_MS);
          return;
        }
        
        // Check if already visible using getBoundingClientRect (viewport-relative)
        const citedRect = cited.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const isVisible = citedRect.top >= containerRect.top && citedRect.bottom <= containerRect.bottom;
        
        if (isVisible) {
          console.log(`[CITATION-SCROLL] Cited segment already visible, skipping scroll`);
          return;
        }
        
        // If the cited element is taller than the container, scroll to its top (with small offset)
        // Otherwise, center it in the container
        let targetScroll: number;
        if (citedRect.height > containerRect.height) {
          // Large segment: scroll to put the top of the cited element near the top of the container
          targetScroll = container.scrollTop + (citedRect.top - containerRect.top) - 20; // 20px padding from top
        } else {
          // Normal segment: center it
          targetScroll = container.scrollTop + (citedRect.top - containerRect.top) - (containerRect.height / 2) + (citedRect.height / 2);
        }
        console.log(`[CITATION-SCROLL] Scrolling. citedHeight=${citedRect.height}, containerHeight=${containerRect.height}, targetScroll=${targetScroll}`);
        container.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
        return;  // Success — element exists
      }

      if (attempts < MAX_ATTEMPTS) {
        timeoutId = setTimeout(tryScroll, INTERVAL_MS);
      }
    };

    // Wait for panel to mount and segments to render
    timeoutId = setTimeout(tryScroll, 200);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [citationPanel, citationLoading]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
      if (speakerDropdownRef.current && !speakerDropdownRef.current.contains(e.target as Node)) {
        setSpeakerDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Left sidebar resize handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || !isResizingLeft) return;
      const rect = containerRef.current.getBoundingClientRect();
      setLeftSidebarWidth(Math.max(200, Math.min(500, e.clientX - rect.left)));
    };
    const handleMouseUp = () => {
      setIsResizingLeft(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    if (isResizingLeft) {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingLeft]);

  // Right panel resize handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || !isResizingRight) return;
      const rect = containerRef.current.getBoundingClientRect();
      setRightPanelWidth(Math.max(280, Math.min(600, rect.right - e.clientX)));
    };
    const handleMouseUp = () => {
      setIsResizingRight(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    if (isResizingRight) {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingRight]);

  // ── Sidebar hover ──
  const handleSidebarMouseEnter = () => {
    if (!sidebarPinned) {
      if (sidebarTimeoutRef.current) clearTimeout(sidebarTimeoutRef.current);
      setSidebarHovered(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    if (!sidebarPinned) {
      sidebarTimeoutRef.current = setTimeout(() => setSidebarHovered(false), 300);
    }
  };

  // ── Folder/episode selection ──
  const toggleFolder = (folderId: string) => {
    const folder = folders.find((f) => f.id === folderId);
    const wasSelected = selectedFolders.has(folderId);

    setSelectedFolders((prev) => {
      const next = new Set(prev);
      wasSelected ? next.delete(folderId) : next.add(folderId);
      return next;
    });
    // Speaker selection is now global - don't clear on folder change

    if (folder?.episodes) {
      setSelectedItems((prev) => {
        const next = new Set(prev);
        folder.episodes!.forEach((ep) => wasSelected ? next.delete(ep.id) : next.add(ep.id));
        return next;
      });
    }
  };

  const toggleEpisode = (folderId: string, episodeId: string) => {
    const folder = folders.find((f) => f.id === folderId);

    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.has(episodeId) ? next.delete(episodeId) : next.add(episodeId);

      if (folder?.episodes) {
        const allSel = folder.episodes.every((ep) => next.has(ep.id));
        const noneSel = folder.episodes.every((ep) => !next.has(ep.id));
        setSelectedFolders((pf) => {
          const nf = new Set(pf);
          if (allSel) nf.add(folderId);
          else if (noneSel) nf.delete(folderId);
          return nf;
        });
      }
      return next;
    });
  };

  const expandFolder = async (folderId: string) => {
    if (expandedFolders.has(folderId)) {
      setExpandedFolders((prev) => {
        const n = new Set(prev);
        n.delete(folderId);
        return n;
      });
      return;
    }

    setExpandedFolders((prev) => new Set(prev).add(folderId));
    const folder = folders.find((f) => f.id === folderId);
    if (folder?.episodes) return;

    setLoadingEpisodes((prev) => new Set(prev).add(folderId));
    try {
      const res = await fetch(`${API_BASE}/folders/${folderId}/items`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setFolders((prev) => prev.map((f) => f.id === folderId ? { ...f, episodes: data.items || [] } : f));
      if (selectedFolders.has(folderId) && data.items) {
        setSelectedItems((prev) => {
          const n = new Set(prev);
          data.items.forEach((ep: Episode) => n.add(ep.id));
          return n;
        });
      }
    } catch (err) {
      console.error("Failed to load episodes:", err);
    } finally {
      setLoadingEpisodes((prev) => {
        const n = new Set(prev);
        n.delete(folderId);
        return n;
      });
    }
  };

  const selectAll = () => {
    setSelectedFolders(new Set(folders.map((f) => f.id)));
    const all = new Set<string>();
    folders.forEach((f) => f.episodes?.forEach((ep) => all.add(ep.id)));
    setSelectedItems(all);
  };

  const selectNone = () => {
    setSelectedFolders(new Set());
    setSelectedItems(new Set());
    // Speaker selection is now global - don't clear when folders cleared
  };

  // ── Session persistence ──
  // Save current session to localStorage when it changes
  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem('tempo_chat_session_id', currentSessionId);
    } else {
      localStorage.removeItem('tempo_chat_session_id');
    }
  }, [currentSessionId]);

  // Ref to latest loadSession to avoid stale closures in event handlers
  const loadSessionRef = useRef<(id: string) => Promise<void>>();

  // Restore session when returning to app (mobile tab switch / background)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const storedSessionId = localStorage.getItem('tempo_chat_session_id');
        if (storedSessionId && loadSessionRef.current) {
          await loadSessionRef.current(storedSessionId);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Auto-load last session on mount (after sessions are fetched)
  // Skip if navigating with ?item= or ?items= URL param (user wants fresh chat with specific item(s))
  useEffect(() => {
    console.log('[restore] itemIdFromUrl:', itemIdFromUrl, 'itemIdsFromUrl:', itemIdsFromUrl, 'skipping:', !!(itemIdFromUrl || itemIdsFromUrl));
    // Don't auto-restore session if user is opening chat for specific item(s)
    if (itemIdFromUrl || itemIdsFromUrl) return;
    
    const storedSessionId = localStorage.getItem('tempo_chat_session_id');
    if (storedSessionId && sessions.length > 0 && !currentSessionTitle) {
      // Verify the session still exists
      const sessionExists = sessions.some(s => s.id === storedSessionId);
      if (sessionExists) {
        // Set flag BEFORE loadSession to prevent race with auto-select in data fetch
        foldersSetBySession.current = true;
        loadSession(storedSessionId);
      } else {
        // Session was deleted, clear storage
        localStorage.removeItem('tempo_chat_session_id');
      }
    }
  }, [sessions, itemIdFromUrl, itemIdsFromUrl]); // Run when sessions are loaded

  // ── Session management ──
  const loadSession = async (sessionId: string) => {
    // Set flag IMMEDIATELY to prevent race with auto-select
    foldersSetBySession.current = true;
    
    try {
      // Fetch session and folders in parallel to ensure folders are available
      const [sessionRes, foldersRes] = await Promise.all([
        fetch(`${API_BASE}/chat/sessions/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/chat/folders`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const data = await sessionRes.json();
      const _foldersData = await foldersRes.json();
      
      if (data.session && data.messages) {
        setCurrentSessionId(sessionId);
        setCurrentSessionTitle(data.session.title || null);
        setMessages(data.messages.map((m: any) => ({ 
          role: m.role, 
          content: m.content, 
          citations: m.citations_json ? JSON.parse(m.citations_json) : undefined,
          web_citations: m.web_citations_json ? JSON.parse(m.web_citations_json) : undefined,
        })));
        
        // Always reset selections first, then restore from session
        // This prevents previous session's selections from persisting
        let restoredFolderIds: string[] = [];
        let restoredItemIds: string[] = [];
        
        if (data.session.folder_ids_json) {
          const folderData = data.session.folder_ids_json;
          restoredFolderIds = typeof folderData === 'string' ? JSON.parse(folderData) : folderData;
        }
        if (data.session.item_ids_json) {
          const itemData = data.session.item_ids_json;
          restoredItemIds = typeof itemData === 'string' ? JSON.parse(itemData) : itemData;
        }
        
        // If session has no saved context, leave empty (user must select)
        // Previously defaulted to all folders which broke single-episode chats
        if (restoredFolderIds.length === 0 && restoredItemIds.length === 0) {
          setSelectedFolders(new Set());
          setSelectedItems(new Set());
        } else {
          setSelectedFolders(new Set(restoredFolderIds));
          setSelectedItems(new Set(restoredItemIds));
        }
        // Flag already set at function start - kept true
      }
    } catch (err) {
      console.error("Failed to load session:", err);
      // Reset flag on error so auto-select can work
      foldersSetBySession.current = false;
    }
  };

  // Keep ref updated for use in event handlers (avoids stale closures)
  loadSessionRef.current = loadSession;

  // Fetch standalone items (selectedItems not found in any loaded folder.episodes)
  useEffect(() => {
    const loadedItemIds = new Set<string>();
    folders.forEach((f) => {
      f.episodes?.forEach((ep) => loadedItemIds.add(ep.id));
    });
    
    const orphanedIds = Array.from(selectedItems).filter((id) => !loadedItemIds.has(id));
    
    if (orphanedIds.length === 0) {
      setStandaloneItems([]);
      return;
    }
    
    // Fetch metadata for orphaned items
    const fetchOrphanedItems = async () => {
      const items: Array<{ id: string; title: string; folder_id?: string }> = [];
      for (const itemId of orphanedIds) {
        try {
          const res = await fetch(`${API_BASE}/library/items/${itemId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            items.push({ id: data.id, title: data.title, folder_id: data.folder_id });
          }
        } catch {
          // Skip items we can't fetch
        }
      }
      setStandaloneItems(items);
    };
    
    fetchOrphanedItems();
  }, [selectedItems, folders, token]);

  const startNewChat = () => {
    setCurrentSessionId(null);
    setCurrentSessionTitle(null);
    setMessages([]);
    setEditingTitle(false);
    // Clear selections so new chat starts fresh
    setSelectedFolders(new Set());
    setSelectedItems(new Set());
    setStandaloneItems([]);
    // Keep speaker selection - it's a user preference, not tied to chat context
    // Reset flag so auto-select logic can work for fresh chats
    foldersSetBySession.current = false;
  };

  const saveTitle = async () => {
    if (!currentSessionId || !titleInput.trim()) return;
    try {
      await fetch(`${API_BASE}/chat/sessions/${currentSessionId}/title`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: titleInput.trim() })
      });
      setCurrentSessionTitle(titleInput.trim());
      setSessions((prev) => prev.map((s) => s.id === currentSessionId ? { ...s, title: titleInput.trim() } : s));
    } catch (err) {
      console.error("Failed to save title:", err);
    }
    setEditingTitle(false);
  };

  const deleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE}/chat/sessions/${sessionId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setArchivedSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (currentSessionId === sessionId) startNewChat();
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  };

  const archiveSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE}/chat/sessions/${sessionId}/archive`, { 
        method: "POST", 
        headers: { Authorization: `Bearer ${token}` } 
      });
      // Move from active to archived
      const session = sessions.find(s => s.id === sessionId);
      if (session) {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        setArchivedSessions((prev) => [session, ...prev]);
      }
      if (currentSessionId === sessionId) startNewChat();
    } catch (err) {
      console.error("Failed to archive session:", err);
    }
  };

  const unarchiveSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE}/chat/sessions/${sessionId}/unarchive`, { 
        method: "POST", 
        headers: { Authorization: `Bearer ${token}` } 
      });
      // Move from archived to active
      const session = archivedSessions.find(s => s.id === sessionId);
      if (session) {
        setArchivedSessions((prev) => prev.filter((s) => s.id !== sessionId));
        setSessions((prev) => [session, ...prev]);
      }
    } catch (err) {
      console.error("Failed to unarchive session:", err);
    }
  };

  const copyResponse = async (content: string, index: number) => {
    await navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyEntireChat = async () => {
    const t = messages.map((m) => `${m.role === "user" ? "You" : "Assistant"}: ${m.content}`).join("\n\n");
    await navigator.clipboard.writeText(t);
    setCopiedChat(true);
    setTimeout(() => setCopiedChat(false), 2000);
  };

  const openCitation = async (citation: Citation) => {
    console.log('[CITATION-CLICK] Full citation object:', JSON.stringify(citation, null, 2));
    console.log('[CITATION-SCROLL] openCitation called. Current rightPanelMode:', rightPanelMode);
    console.log('OPEN CITATION:', { itemId: citation.item_id, startMs: citation.start_ms });
    
    // Find the LLM claim text that references this citation number
    // Search ALL assistant messages (in reverse) since user might click citation from earlier response
    let claimText = '';
    const assistantMsgs = messages.filter(m => m.role === 'assistant');
    for (const msg of assistantMsgs.reverse()) {
      const paragraphs = msg.content.split('\n\n');
      for (const para of paragraphs) {
        if (para.includes(`[${citation.number}]`)) {
          claimText = para;
          break;
        }
      }
      if (claimText) break;
    }
    console.log('[CITATION-HIGHLIGHT] claimText found:', claimText ? claimText.substring(0, 100) : 'EMPTY', 'searching for [' + citation.number + ']');
    setCitedClaimText(claimText);
    
    setCitationLoading(true);
    setRightPanelMode("citation");
    setAudioAvailable(false);
    setAudioVideoId(null);
    setAudioDirectUrl(null);
    try {
      // Pass speaker for accurate segment matching (avoids picking wrong speaker's adjacent segment)
      const speakerParam = citation.speaker ? `&speaker=${encodeURIComponent(citation.speaker)}` : '';
      const res = await fetch(`${API_BASE}/chat/citation/${citation.item_id}?start_ms=${citation.start_ms}${speakerParam}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      console.log('CITATION DATA:', { citedMs: data.cited_ms, segmentCount: data.segments?.length, citedSegments: data.segments?.filter((s: any) => s.is_cited)?.length });
      console.log('[CITATION-SCROLL] Setting citationPanel now...');
      setCitationPanel(data);
      // Save full snippet for phrase highlighting within the cited segment
      setCitedSnippet(citation.snippet || null);
      // Bug 4: Scroll handled by useEffect on citationPanel change (removed competing requestAnimationFrame)
      
      // Check audio availability for this citation's item
      if (data.video_id && data.video_id.startsWith("rss_")) {
        setAudioLoading(true);
        try {
          const audioRes = await fetch(`${API_BASE}/audio/${encodeURIComponent(data.video_id)}/info`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (audioRes.ok) {
            const audioData = await audioRes.json();
            const isAvailable = audioData.available && audioData.playback_allowed !== false;
            setAudioAvailable(isAvailable);
            if (isAvailable) {
              setAudioVideoId(data.video_id);
              // Set duration from backend metadata (while audio loads)
              if (audioData.duration_seconds) {
                setAudioDuration(audioData.duration_seconds * 1000);
              }
              // Preload audio for instant playback (pass direct URL for RSS streams)
              // Deferred to avoid blocking UI thread during panel render (155MB+ files)
              const directUrl = audioData.stream_type === 'redirect' ? audioData.playback_url : undefined;
              setTimeout(() => audioManager.preloadAudio(data.video_id, directUrl), 0);
              setAudioDirectUrl(directUrl || null);  // Bug 1 fix
              // Auto-seek to cited timestamp (handles loading state)
              console.log('AUDIO SEEK: citation start_ms=', citation.start_ms, 'seeking to', citation.start_ms / 1000, 's');
              seekAudioTo(citation.start_ms);
            }
          }
        } catch {
          setAudioAvailable(false);
        } finally {
          setAudioLoading(false);
        }
      }
      // Scroll is handled by useEffect when citationPanel updates
    } catch (err) {
      console.error("Failed to load citation context:", err);
    } finally {
      setCitationLoading(false);
    }
  };

  const openWebCitation = async (webCitation: WebCitation) => {
    setRightPanelMode("web-citation");
    setWebCitationPanel(webCitation);
    // Clear audio state when viewing web citation
    setAudioAvailable(false);
    setAudioVideoId(null);
    setAudioDirectUrl(null);
    
    // Reset reader state
    setWebProxyContent(null);
    setWebProxyTitle(null);
    setWebProxyError(null);
    setWebProxyStatus('idle');
    
    // Skip proxy fetch for X/Twitter sources - use full_content directly
    const isTwitterSource = webCitation.domain === 'x.com' || 
                            webCitation.domain === 'twitter.com' ||
                            webCitation.type === 'x_search' ||
                            webCitation.url?.includes('x.com') ||
                            webCitation.url?.includes('twitter.com');
    
    if (isTwitterSource) {
      // Use full_content for Twitter/X sources (Grok summaries)
      if (webCitation.full_content) {
        setWebProxyContent(webCitation.full_content);
        setWebProxyTitle(webCitation.title || 'X/Twitter Summary');
        setWebProxyStatus('loaded');
      }
      return;
    }
    
    // If citation has a URL, fetch the actual page content
    if (webCitation.url) {
      const fetchUrl = webCitation.url;
      latestWebFetchRef.current = fetchUrl;  // Race condition guard
      setWebProxyStatus('loading');
      
      try {
        const res = await fetch(
          `${API_BASE}/proxy/fetch?url=${encodeURIComponent(fetchUrl)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Discard if user clicked a different citation while loading
        if (latestWebFetchRef.current !== fetchUrl) return;
        
        if (res.ok) {
          const data = await res.json();
          if (data.error) {
            setWebProxyStatus('error');
            setWebProxyError(data.error);
          } else {
            setWebProxyContent(data.content);
            setWebProxyTitle(data.title || webCitation.title);
            setWebProxyStatus('loaded');
          }
        } else {
          setWebProxyStatus('error');
          setWebProxyError('fetch_failed');
        }
      } catch (err) {
        if (latestWebFetchRef.current !== fetchUrl) return;
        setWebProxyStatus('error');
        setWebProxyError('network');
      }
    }
  };

  const toggleTranscriptPanel = () => {
    if (rightPanelMode === "transcripts") {
      setRightPanelMode("closed");
      setAudioAvailable(false);
      setAudioVideoId(null);
      setAudioDirectUrl(null);
    } else {
      setViewingTranscript(null);
      setAudioAvailable(false);
      setAudioVideoId(null);
      setAudioDirectUrl(null);
      setRightPanelMode("transcripts");
    }
  };

  const closeRightPanel = () => {
    setRightPanelMode("closed");
    setCitationPanel(null);
    setCitedSnippet(null);
    setCitedClaimText('');
    setWebCitationPanel(null);
    setWebProxyContent(null);
    setWebProxyStatus('idle');
    setViewingTranscript(null);
    setAudioAvailable(false);
    setAudioVideoId(null);
    setAudioDirectUrl(null);
  };

  const expandRightFolder = async (folderId: string) => {
    if (rightExpandedFolders.has(folderId)) {
      setRightExpandedFolders((prev) => { const n = new Set(prev); n.delete(folderId); return n; });
      return;
    }
    setRightExpandedFolders((prev) => new Set(prev).add(folderId));
    const folder = folders.find((f) => f.id === folderId);
    if (folder?.episodes) return;

    setRightLoadingFolders((prev) => new Set(prev).add(folderId));
    try {
      const res = await fetch(`${API_BASE}/folders/${folderId}/items`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setFolders((prev) => prev.map((f) => f.id === folderId ? { ...f, episodes: data.items || [] } : f));
      if (selectedFolders.has(folderId) && data.items) {
        setSelectedItems((prev) => {
          const n = new Set(prev);
          data.items.forEach((ep: Episode) => n.add(ep.id));
          return n;
        });
      }
    } catch (err) {
      console.error("Failed to load episodes:", err);
    } finally {
      setRightLoadingFolders((prev) => { const n = new Set(prev); n.delete(folderId); return n; });
    }
  };

  // Seek audio to specific time in milliseconds
  const seekAudioToMs = (ms: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = ms / 1000;
      // Auto-play when seeking via timestamp click
      audioRef.current.play().catch(() => {});
    }
  };

  const viewEpisodeTranscript = async (episodeId: string, episodeTitle: string) => {
    setTranscriptLoading(true);
    setViewingTranscript(null);
    setAudioAvailable(false);
    setAudioVideoId(null);
    setAudioDirectUrl(null);  // Bug 1 fix
    try {
      const res = await fetch(`${API_BASE}/library/items/${episodeId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const item = data.item || data;
      
      // Issue 3: Parse, normalize, and fallback fetch for RSS/clip items
      let segments = item.transcript_json ? JSON.parse(item.transcript_json) : [];
      segments = normalizeSegments(segments);
      
      if (segments.length === 0) {
        try {
          const segData = await apiFetch<{ segments: any[] }>(
            `/chat/citation/${episodeId}?start_ms=0`
          );
          if (segData.segments && segData.segments.length > 0) {
            segments = normalizeSegments(segData.segments);
          }
        } catch (segErr) {
          console.warn('Fallback segment fetch failed:', segErr);
        }
      }
      
      setViewingTranscript({ id: episodeId, title: item.title || episodeTitle, segments, thumbnail_url: item.thumbnail_url });
      
      // Check audio availability
      if (item.video_id && (item.source === "rss" || item.video_id.startsWith("rss_"))) {
        setAudioLoading(true);
        try {
          const audioRes = await fetch(`${API_BASE}/audio/${encodeURIComponent(item.video_id)}/info`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (audioRes.ok) {
            const audioData = await audioRes.json();
            const isAvailable = audioData.available && audioData.playback_allowed !== false;
            setAudioAvailable(isAvailable);
            if (isAvailable) {
              setAudioVideoId(item.video_id);
              // Bug 1 fix: store direct URL for RSS streams
              const directUrl = audioData.stream_type === 'redirect' ? audioData.playback_url : undefined;
              audioManager.preloadAudio(item.video_id, directUrl);
              setAudioDirectUrl(directUrl || null);
            }
          }
        } catch {
          setAudioAvailable(false);
        } finally {
          setAudioLoading(false);
        }
      }
    } catch (err) {
      console.error("Failed to load transcript:", err);
    } finally {
      setTranscriptLoading(false);
    }
  };

  // Streaming chat message handler (supports both standard and research mode)
  const sendMessageStreaming = async () => {
    const canSend = selectedFolders.size > 0 || selectedItems.size > 0 || (currentSessionId && messages.length > 0);
    if (!query.trim() || loading || !canSend) return;

    const userMessage = query.trim();
    const isNew = !currentSessionId;

    setQuery("");
    if (inputRef.current) {
      inputRef.current.style.height = '44px';
    }
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);
    setActiveTools([]); // Clear any previous tool state

    // Add placeholder assistant message for streaming
    setMessages((prev) => [...prev, { role: "assistant", content: "", research_mode: researchMode }]);

    try {
      const mc = llmModels.find((m) => m.model === selectedModel);
      const response = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}`,
          "Accept": "text/event-stream"
        },
        body: JSON.stringify({
          message: userMessage,
          folder_ids: Array.from(selectedFolders),
          // Include singleItemContext.id if set (ensures single-episode context is persisted)
          item_ids: singleItemContext?.id 
            ? [...new Set([...Array.from(selectedItems), singleItemContext.id])]
            : Array.from(selectedItems),
          session_id: currentSessionId,
          provider: mc?.provider,
          model: mc?.model,
          research_mode: researchMode,
          speaker_id: selectedSpeaker,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          const detail = errorData.detail || errorData;
          if (detail.verification_required) {
            setVerificationLimitType('chat');
            setVerificationLimits({ limit: detail.messages_limit || 10, bonus: 140 });
            setShowVerificationModal(true);
            setMessages((prev) => prev.slice(0, -2)); // Remove user + assistant placeholder
            setLoading(false);
            return;
          }
        }
        throw new Error(errorData.detail || 'Stream request failed');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let streamedContent = "";
      let citations: Citation[] = [];
      let webCitations: WebCitation[] = [];
      let newSessionId: string | null = null;
      let unitsConsumed = 0;
      let balanceAfter: number | undefined;

      if (reader) {
        let buffer = "";
        
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue;
              
              try {
                const event = JSON.parse(jsonStr);
                
                // Research mode: tool_start event
                if (event.type === "tool_start") {
                  setActiveTools((prev) => [...prev, {
                    tool: event.tool,
                    query: event.query,
                    icon: event.icon || "🔧",
                    label: event.label || "Processing",
                    status: 'running'
                  }]);
                }
                // Research mode: tool_done event
                else if (event.type === "tool_done") {
                  setActiveTools((prev) => prev.map((t) => 
                    t.tool === event.tool && t.status === 'running'
                      ? { ...t, status: event.status === 'error' ? 'error' : 'done', summary: event.summary }
                      : t
                  ));
                }
                // Content chunk
                else if (event.type === "chunk" && event.content) {
                  streamedContent += event.content;
                  // Clear active tools when content starts streaming (research is done)
                  if (streamedContent.length === event.content.length) {
                    // First chunk - keep tools visible but they're already marked done
                  }
                  // Update message content incrementally
                  setMessages((prev) => {
                    const updated = [...prev];
                    if (updated.length > 0) {
                      updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        content: streamedContent
                      };
                    }
                    return updated;
                  });
                }
                // Transcript citations
                else if (event.type === "citations") {
                  citations = event.citations || [];
                }
                // Web citations (research mode)
                else if (event.type === "web_citations") {
                  webCitations = event.web_citations || [];
                }
                // Done event
                else if (event.type === "done") {
                  newSessionId = event.session_id || null;
                  unitsConsumed = event.units_consumed || 0;
                  balanceAfter = event.balance_after;
                  
                  // Use renumbered full_response if available, else fall back to streamed content
                  const finalContent = event.full_response || streamedContent;
                  
                  // Final update with all data
                  setMessages((prev) => {
                    const updated = [...prev];
                    if (updated.length > 0) {
                      updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        content: finalContent,
                        citations: citations,
                        web_citations: webCitations.length > 0 ? webCitations : undefined,
                        units_consumed: unitsConsumed,
                        balance_after: balanceAfter,
                        research_mode: event.research_mode || researchMode
                      };
                    }
                    return updated;
                  });
                  
                  // Clear active tools after done
                  setActiveTools([]);
                }
                // Error event
                else if (event.type === "error") {
                  setActiveTools([]);
                  setMessages((prev) => {
                    const updated = [...prev];
                    if (updated.length > 0) {
                      updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        content: `Error: ${event.message || 'Unknown error'}`
                      };
                    }
                    return updated;
                  });
                }
              } catch (e) {
                console.error("Failed to parse SSE event:", e, jsonStr);
              }
            }
          }
        }
      }

      // Update session info
      if (newSessionId) {
        setCurrentSessionId(newSessionId);
        if (isNew) {
          const sr = await fetch(`${API_BASE}/chat/sessions`, { headers: { Authorization: `Bearer ${token}` } });
          const sd = await sr.json();
          setSessions(sd || []);
          const ns = sd?.find((s: ChatSession) => s.id === newSessionId);
          if (ns?.title) setCurrentSessionTitle(ns.title);
        }
      }

      // Update usage
      if (balanceAfter !== undefined) {
        setResearchBalance((prev) => prev ? { ...prev, balance: balanceAfter } : prev);
        setChatUsage((prev) => prev ? { 
          ...prev, 
          chat: { ...prev.chat, used: prev.chat.used + 1 },
          research: { ...prev.research, balance: balanceAfter }
        } : prev);
      } else {
        setChatUsage((prev) => prev ? { 
          ...prev, 
          chat: { ...prev.chat, used: prev.chat.used + 1 }
        } : prev);
      }

    } catch (e) {
      console.error("Streaming error:", e);
      setActiveTools([]);
      setMessages((prev) => {
        const updated = [...prev];
        if (updated.length > 0 && updated[updated.length - 1].role === "assistant") {
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: updated[updated.length - 1].content || `Failed to send message: ${e}`
          };
        }
        return updated;
      });
    } finally {
      setLoading(false);
      setActiveTools([]);
    }
  };


  // Main send handler - use streaming for both modes now
  const sendMessage = async () => {
    // Use streaming for both research and standard mode
    await sendMessageStreaming();
  };

  const editAndResend = async (index: number) => {
    if (!editText.trim() || loading) return;
    
    const userMessage = editText.trim();
    setEditingIndex(null);
    setEditText("");
    
    // Truncate messages to before this message, then add edited version
    setMessages((prev) => [...prev.slice(0, index), { role: "user", content: userMessage }]);
    setLoading(true);

    // Use streaming for non-research mode
    if (!researchMode) {
      // Add placeholder for streaming
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);
      
      try {
        const mc = llmModels.find((m) => m.model === selectedModel);
        const response = await fetch(`${API_BASE}/chat/stream`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json", 
            Authorization: `Bearer ${token}`,
            "Accept": "text/event-stream"
          },
          body: JSON.stringify({
            message: userMessage,
            folder_ids: Array.from(selectedFolders),
            item_ids: singleItemContext?.id ? [...new Set([...Array.from(selectedItems), singleItemContext.id])] : Array.from(selectedItems),
            session_id: currentSessionId,
            provider: mc?.provider,
            model: mc?.model,
            speaker_id: selectedSpeaker,
          }),
        });

        if (!response.ok) {
          throw new Error('Stream request failed');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let streamedContent = "";
        let citations: Citation[] = [];

        if (reader) {
          let buffer = "";
          
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                const jsonStr = line.slice(6).trim();
                if (!jsonStr) continue;
                
                try {
                  const event = JSON.parse(jsonStr);
                  
                  if (event.type === "chunk" && event.content) {
                    streamedContent += event.content;
                    setMessages((prev) => {
                      const updated = [...prev];
                      if (updated.length > 0) {
                        updated[updated.length - 1] = {
                          ...updated[updated.length - 1],
                          content: streamedContent
                        };
                      }
                      return updated;
                    });
                  } else if (event.type === "citations") {
                    citations = event.citations || [];
                  } else if (event.type === "done") {
                    // Use renumbered full_response if available
                    const finalContent = event.full_response || streamedContent;
                    setMessages((prev) => {
                      const updated = [...prev];
                      if (updated.length > 0) {
                        updated[updated.length - 1] = {
                          ...updated[updated.length - 1],
                          content: finalContent,
                          citations: citations
                        };
                      }
                      return updated;
                    });
                  } else if (event.type === "error") {
                    setMessages((prev) => {
                      const updated = [...prev];
                      if (updated.length > 0) {
                        updated[updated.length - 1] = {
                          ...updated[updated.length - 1],
                          content: `Error: ${event.message || 'Unknown error'}`
                        };
                      }
                      return updated;
                    });
                  }
                } catch (e) {
                  console.error("Failed to parse SSE event:", e);
                }
              }
            }
          }
        }

        setChatUsage((prev) => prev ? { 
          ...prev, 
          chat: { ...prev.chat, used: prev.chat.used + 1 }
        } : prev);

      } catch {
        setMessages((prev) => [...prev, { role: "assistant", content: "Failed to send message" }]);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Non-streaming path for research mode
    try {
      const mc = llmModels.find((m) => m.model === selectedModel);
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          message: userMessage,
          folder_ids: Array.from(selectedFolders),
          item_ids: singleItemContext?.id ? [...new Set([...Array.from(selectedItems), singleItemContext.id])] : Array.from(selectedItems),
          session_id: currentSessionId,
          provider: mc?.provider,
          model: mc?.model,
          research_mode: researchMode,
          speaker_id: selectedSpeaker,
        }),
      });

      const data = await res.json();

      // Handle 429 rate limit errors (including verification required)
      if (res.status === 429) {
        const detail = data.detail || data;
        if (detail.verification_required) {
          setVerificationLimitType('chat');
          setVerificationLimits({
            limit: detail.messages_limit || 10,
            bonus: 140
          });
          setShowVerificationModal(true);
        } else {
          setMessages((prev) => [...prev, { 
            role: "assistant", 
            content: `Chat limit reached: ${detail.message || 'Monthly limit exceeded'}. ${detail.upgrade_hint || ''}` 
          }]);
        }
        setLoading(false);
        return;
      }

      if (data.error) {
        setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${data.error}` }]);
      } else {
        setMessages((prev) => [...prev, { 
          role: "assistant", 
          content: data.response || "No response", 
          citations: data.citations,
          web_citations: data.web_citations,
          units_consumed: data.units_consumed,
          balance_after: data.balance_after,
          research_mode: researchMode
        }]);

        if (data.balance_after !== undefined) {
          setResearchBalance((prev) => prev ? { ...prev, balance: data.balance_after } : prev);
          setChatUsage((prev) => prev ? { 
            ...prev, 
            chat: { ...prev.chat, used: prev.chat.used + 1 },
            research: { ...prev.research, balance: data.balance_after }
          } : prev);
        } else {
          setChatUsage((prev) => prev ? { 
            ...prev, 
            chat: { ...prev.chat, used: prev.chat.used + 1 }
          } : prev);
        }
      }
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Failed to send message" }]);
    } finally {
      setLoading(false);
    }
  };

  const totalEpisodes = (() => {
    let count = 0;
    const counted = new Set<string>();
    folders.forEach((f) => {
      if (selectedFolders.has(f.id)) {
        count += f.episode_count;
        f.episodes?.forEach((ep) => counted.add(ep.id));
      }
    });
    selectedItems.forEach((id) => {
      if (!counted.has(id)) count++;
    });
    return count;
  })();

  // Has context: either folders selected OR continuing an existing session
  const hasSelection = selectedFolders.size > 0 || selectedItems.size > 0 || (currentSessionId && messages.length > 0);

  /* ═══════════════════════════════════════════
     RENDER HELPERS
     ═══════════════════════════════════════════ */

  const renderCollapsedSidebar = () => (
    <div className="flex flex-col items-center py-3 gap-1">
      <button onClick={() => { setSidebarPinned(true); startNewChat(); }} className="flex items-center justify-center w-9 h-9 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors" title="New Chat">
        <Plus className="w-[18px] h-[18px]" />
      </button>
      <div className="my-1 w-5 h-px bg-gray-200" />
      <button onClick={() => { setSidebarPinned(true); setSessionsCollapsed(false); }} className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors relative" title={`Saved Chats (${sessions.length})`}>
        <MessageSquare className="w-[18px] h-[18px]" strokeWidth={1.5} />
        {sessions.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">{sessions.length > 9 ? "9+" : sessions.length}</span>}
      </button>
      <div className="my-1 w-5 h-px bg-gray-200" />
      <button onClick={() => { setSidebarPinned(true); setFoldersCollapsed(false); }} className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors relative" title={`Folders (${selectedFolders.size} selected)`}>
        <FolderOpen className="w-[18px] h-[18px]" strokeWidth={1.5} />
        {selectedFolders.size > 0 && <span className="absolute -top-0.5 -right-0.5 bg-green-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">{selectedFolders.size > 9 ? "9+" : selectedFolders.size}</span>}
      </button>
    </div>
  );

  const renderExpandedSidebar = () => (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-1 shrink-0">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Chat</span>
        <button onClick={() => setSidebarPinned(!sidebarPinned)} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title={sidebarPinned ? "Unpin sidebar" : "Pin sidebar"}>
          {sidebarPinned ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
        </button>
      </div>

      {/* New Chat */}
      <div className="px-3 pb-2 shrink-0">
        <button onClick={startNewChat} className="w-full min-h-[38px] rounded-lg px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> New Chat
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 min-h-0">
        {/* Saved Chats */}
        <button onClick={() => setSessionsCollapsed(!sessionsCollapsed)} className="flex items-center gap-1 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-700 w-full mb-1">
          <ChevronDown className={`h-3.5 w-3.5 transition-transform shrink-0 ${sessionsCollapsed ? "-rotate-90" : ""}`} />
          <span className="flex-1 text-left">Saved Chats</span>
          <span className="text-xs text-gray-400 font-normal normal-case">{sessions.length}</span>
        </button>

        {!sessionsCollapsed && (
          <div className="space-y-0.5 mb-3">
            {isSessionExpired ? <SessionExpiredBanner /> : sessions.length === 0 ? <p className="px-2 py-2 text-sm text-gray-400">No saved chats yet</p> : sessions.map((session) => (
              <div key={session.id} onClick={() => loadSession(session.id)} onTouchEnd={(e) => { e.preventDefault(); loadSession(session.id); }} role="button" tabIndex={0} className={`group w-full min-h-[34px] rounded-md px-2 py-1.5 text-left text-sm font-normal flex items-center gap-2 transition-colors cursor-pointer ${currentSessionId === session.id ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-700 hover:bg-gray-100"}`}>
                <MessageSquare className="w-3.5 h-3.5 opacity-60 shrink-0" strokeWidth={1.5} />
                <span className="flex-1 truncate">{session.title || "Untitled"}</span>
                <button onClick={(e) => archiveSession(session.id, e)} onTouchEnd={(e) => { e.stopPropagation(); archiveSession(session.id, e as any); }} className="p-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-gray-400 hover:text-amber-600 active:text-amber-700 transition-opacity shrink-0 touch-manipulation" title="Archive">
                  <Archive className="w-4 h-4 sm:w-3 sm:h-3" />
                </button>
                <button onClick={(e) => deleteSession(session.id, e)} onTouchEnd={(e) => { e.stopPropagation(); deleteSession(session.id, e as any); }} className="p-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-gray-400 hover:text-red-500 active:text-red-600 transition-opacity shrink-0 touch-manipulation" title="Delete">
                  <Trash2 className="w-4 h-4 sm:w-3 sm:h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Archived Chats */}
        {archivedSessions.length > 0 && (
          <>
            <button onClick={() => setArchivedCollapsed(!archivedCollapsed)} className="flex items-center gap-1 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-700 w-full mb-1">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform shrink-0 ${archivedCollapsed ? "-rotate-90" : ""}`} />
              <span className="flex-1 text-left">Archived</span>
              <span className="text-xs text-gray-400 font-normal normal-case">{archivedSessions.length}</span>
            </button>

            {!archivedCollapsed && (
              <div className="space-y-0.5 mb-3">
                {archivedSessions.map((session) => (
                  <div key={session.id} onClick={() => loadSession(session.id)} onTouchEnd={(e) => { e.preventDefault(); loadSession(session.id); }} role="button" tabIndex={0} className={`group w-full min-h-[34px] rounded-md px-2 py-1.5 text-left text-sm font-normal flex items-center gap-2 transition-colors cursor-pointer ${currentSessionId === session.id ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-500 hover:bg-gray-100"}`}>
                    <Archive className="w-3.5 h-3.5 opacity-40 shrink-0" strokeWidth={1.5} />
                    <span className="flex-1 truncate opacity-75">{session.title || "Untitled"}</span>
                    <button onClick={(e) => unarchiveSession(session.id, e)} onTouchEnd={(e) => { e.stopPropagation(); unarchiveSession(session.id, e as any); }} className="p-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-gray-400 hover:text-green-600 active:text-green-700 transition-opacity shrink-0 touch-manipulation" title="Restore">
                      <ArchiveRestore className="w-4 h-4 sm:w-3 sm:h-3" />
                    </button>
                    <button onClick={(e) => deleteSession(session.id, e)} onTouchEnd={(e) => { e.stopPropagation(); deleteSession(session.id, e as any); }} className="p-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-gray-400 hover:text-red-500 active:text-red-600 transition-opacity shrink-0 touch-manipulation" title="Delete">
                      <Trash2 className="w-4 h-4 sm:w-3 sm:h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="my-2 h-px bg-gray-200" />

        {/* Folders */}
        <SharedFolderSidebar
          mode="chat"
          folders={folders}
          selectedFolders={selectedFolders}
          selectedItems={selectedItems}
          expandedFolders={expandedFolders}
          loadingEpisodes={loadingEpisodes}
          loadingFolders={loadingFolders}
          onToggleFolder={toggleFolder}
          onToggleEpisode={toggleEpisode}
          onExpandFolder={expandFolder}
          onSelectAll={selectAll}
          onSelectNone={selectNone}
          collapsed={foldersCollapsed}
          onToggleCollapse={() => setFoldersCollapsed(!foldersCollapsed)}
          onFoldersReordered={fetchFolders}
          onCreateFolder={async (name) => {
            await apiFetch("/folders", {
              method: "POST",
              body: JSON.stringify({ name }),
            });
            await fetchFolders();
          }}
        />
      </div>
    </div>
  );

  const renderTranscriptList = () => {
    // Viewing a specific episode's transcript
    if (viewingTranscript || transcriptLoading) {
      return (
        <div className="flex flex-col h-full">
          {/* Header with episode title */}
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 shrink-0">
            <button onClick={() => { setViewingTranscript(null); setAudioAvailable(false); setAudioVideoId(null); setAudioDirectUrl(null); }} className="p-1 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h3 className="text-sm font-medium text-gray-900 truncate flex-1">{viewingTranscript?.title || "Loading..."}</h3>
            <button onClick={closeRightPanel} className="p-1 text-gray-400 hover:text-gray-600 shrink-0"><X className="h-4 w-4" /></button>
          </div>
          
          {/* Audio Player - fixed at top below header */}
          {audioAvailable && audioVideoId && (
            <div className="shrink-0 border-b border-gray-200 bg-white p-3">
              <div className="flex items-center gap-3">
                {/* Play/Pause button */}
                <button
                  onClick={() => {
                    if (audioRef.current) {
                      if (audioRef.current.paused) audioRef.current.play();
                      else audioRef.current.pause();
                    }
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  {audioPlaying ? (
                    <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                  ) : (
                    <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </button>
                {/* Time display */}
                <span className="text-sm text-gray-600 tabular-nums min-w-[4rem]">
                  {Math.floor(audioCurrentTimeMs / 60000)}:{String(Math.floor((audioCurrentTimeMs % 60000) / 1000)).padStart(2, '0')}
                  {audioDuration > 0 && <span className="text-gray-400"> / {Math.floor(audioDuration / 60000)}:{String(Math.floor((audioDuration % 60000) / 1000)).padStart(2, '0')}</span>}
                </span>
                {/* Progress bar */}
                <div 
                  className="flex-1 h-2 bg-gray-200 rounded-full cursor-pointer"
                  onClick={(e) => {
                    if (audioRef.current && audioDuration > 0) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const pct = x / rect.width;
                      audioRef.current.currentTime = (pct * audioDuration) / 1000;
                    }
                  }}
                >
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all"
                    style={{ width: audioDuration > 0 ? `${(audioCurrentTimeMs / audioDuration) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            </div>
          )}
          {audioLoading && (
            <div className="shrink-0 border-b border-gray-200 bg-white p-3">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                Loading audio...
              </div>
            </div>
          )}
          
          {/* Scrollable transcript content */}
          <div className="flex-1 overflow-y-auto p-4">
            {transcriptLoading ? (
              <div className="flex items-center justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" /></div>
            ) : viewingTranscript?.segments.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No transcript segments found</p>
            ) : (
              <div data-panel="transcripts" className="space-y-3">
                {viewingTranscript?.segments.map((seg, i) => {
                  const nextSeg = viewingTranscript.segments[i + 1];
                  const isActive = audioAvailable && audioPlaying && audioCurrentTimeMs >= seg.start_ms && 
                    (!nextSeg || audioCurrentTimeMs < nextSeg.start_ms);
                  return (
                    <div 
                      key={i} 
                      data-start-ms={seg.start_ms} 
                      className={`rounded-lg p-3 transition-all duration-300 ${
                        isActive ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-700">{seg.speaker}</span>
                        {audioAvailable ? (
                          <button
                            onClick={() => seekAudioToMs(seg.start_ms)}
                            className="text-xs text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                            title="Jump to this timestamp"
                          >
                            [{formatMs(seg.start_ms)}]
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">[{formatMs(seg.start_ms)}]</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-800">{seg.text}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {viewingTranscript?.id && (
            <div className="border-t border-gray-200 p-4 shrink-0">
              <a href={`/transcript/${viewingTranscript.id}`} className="flex items-center justify-center gap-2 w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">
                <ExternalLink className="h-4 w-4" /> Open Full Transcript
              </a>
            </div>
          )}
        </div>
      );
    }

    // Folder / episode list
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center border-b border-gray-200 px-4 py-3 shrink-0">
          <button onClick={closeRightPanel} className="p-1.5 -ml-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 md:hidden">
            <X className="h-5 w-5" />
          </button>
          <h3 className="flex-1 text-center text-sm font-semibold text-gray-900 md:text-left md:pl-0">Transcripts</h3>
          <button onClick={closeRightPanel} className="hidden md:block p-1 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {folders
            .filter((f) => selectedFolders.has(f.id) || f.episodes?.some((ep) => selectedItems.has(ep.id)))
            .map((folder) => {
              const isExp = rightExpandedFolders.has(folder.id);
              const isLoad = rightLoadingFolders.has(folder.id);
              const isFolderFullySelected = selectedFolders.has(folder.id);

              return (
                <div key={folder.id} className="mb-3">
                  <button
                    onClick={() => expandRightFolder(folder.id)}
                    className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRight className={`h-3.5 w-3.5 text-gray-400 shrink-0 transition-transform ${isExp ? "rotate-90" : ""}`} />
                    <span className="text-sm font-semibold text-gray-700 flex-1 text-left truncate">{folder.name}</span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {isFolderFullySelected 
                        ? folder.episode_count 
                        : folder.episodes?.filter((ep) => selectedItems.has(ep.id)).length || 0}
                    </span>
                  </button>
                  {isExp && (
                    <div className="ml-5 mt-1 space-y-0.5">
                      {isLoad ? (
                        <p className="px-3 py-1.5 text-sm text-gray-400">Loading episodes...</p>
                      ) : folder.episodes?.length === 0 ? (
                        <p className="px-3 py-1.5 text-sm text-gray-400">No episodes</p>
                      ) : (
                        (isFolderFullySelected ? folder.episodes : folder.episodes?.filter((ep) => selectedItems.has(ep.id)))?.map((ep) => (
                          <button
                            key={ep.id}
                            onClick={() => viewEpisodeTranscript(ep.id, ep.title)}
                            className="flex items-center gap-2 w-full py-1.5 px-3 rounded-md hover:bg-gray-50 text-sm text-gray-700 transition-colors text-left"
                          >
                            <TranscriptIcon className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="truncate flex-1">{ep.title}</span>
                            <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          {/* Standalone items (selected but not in any loaded folder) */}
          {standaloneItems.length > 0 && (
            <div className="mb-3">
              <div className="px-2 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">Selected Episodes</div>
              <div className="space-y-0.5">
                {standaloneItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => viewEpisodeTranscript(item.id, item.title)}
                    className="flex items-center gap-2 w-full py-1.5 px-3 rounded-md hover:bg-gray-50 text-sm text-gray-700 transition-colors text-left"
                  >
                    <TranscriptIcon className="w-3 h-3 text-gray-400 shrink-0" />
                    <span className="truncate flex-1">{item.title}</span>
                    <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {selectedFolders.size === 0 && selectedItems.size === 0 && standaloneItems.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FolderOpen className="w-8 h-8 mb-2" strokeWidth={1} />
              <p className="text-xs">No content selected</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWebCitationPanel = () => {
    if (!webCitationPanel) return null;
    return (
      <div key={`web-citation-${webCitationPanel.number || webCitationPanel.artifact_id}`} className="flex flex-col h-full">
        {/* Header with emerald accent */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-emerald-200 bg-emerald-50 shrink-0">
          <button
            onClick={() => { setRightPanelMode("transcripts"); setWebCitationPanel(null); setWebProxyContent(null); setWebProxyStatus('idle'); }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to transcripts
          </button>
          <div className="flex-1" />
          <button 
            onClick={() => { setRightPanelMode("closed"); setWebCitationPanel(null); setWebProxyContent(null); setWebProxyStatus('idle'); }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Source badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
              🌐 W{webCitationPanel.number}
            </span>
          </div>

          {/* Source preview card */}
          <SourcePreviewCard citation={webCitationPanel} />

          {/* === READER VIEW === */}
          
          {/* Loading state */}
          {webProxyStatus === 'loading' && (
            <div className="flex items-center gap-3 py-8 justify-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
              <span className="text-sm text-gray-500">Loading source...</span>
            </div>
          )}
          
          {/* Loaded: actual page content */}
          {webProxyStatus === 'loaded' && webProxyContent && (
            <div className="mb-4">
              {webProxyTitle && (
                <p className="text-sm font-semibold text-gray-900 mb-2">{webProxyTitle}</p>
              )}
              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                {webProxyContent.split('\n').map((paragraph, i) => {
                  if (!paragraph.trim()) return null;
                  return (
                    <p key={i} className="mb-2 text-sm">{paragraph}</p>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Error: fallback to evidence snippet */}
          {webProxyStatus === 'error' && (
            <div className="mb-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3">
                <p className="text-xs text-amber-700">
                  {webProxyError === 'blocked' && "This URL was blocked for security reasons."}
                  {webProxyError === 'not_html' && "This source is a file (PDF, image, etc.) and cannot be previewed here."}
                  {webProxyError === 'empty' && "Could not extract content from this page."}
                  {webProxyError === 'timeout' && "The page took too long to load."}
                  {(!webProxyError || webProxyError === 'network' || webProxyError === 'fetch_failed') && "Could not load source content."}
                  {' '}
                  {webCitationPanel.url && (
                    <a href={webCitationPanel.url} target="_blank" rel="noopener noreferrer" className="underline font-medium">
                      Open original →
                    </a>
                  )}
                </p>
              </div>
              {/* Fallback: evidence snippet from Perplexity */}
              {webCitationPanel.evidence_snippet && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Summary from research
                  </p>
                  <blockquote className="border-l-2 border-emerald-300 pl-3 py-2 text-sm text-gray-700 italic bg-emerald-50/50 rounded-r">
                    "{webCitationPanel.evidence_snippet}"
                  </blockquote>
                </div>
              )}
            </div>
          )}
          
          {/* Idle (no URL on citation) — show snippet or nothing */}
          {webProxyStatus === 'idle' && !webCitationPanel.url && webCitationPanel.evidence_snippet && (
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Evidence Used in Response
              </p>
              <blockquote className="border-l-2 border-emerald-300 pl-3 py-2 text-sm text-gray-700 italic bg-emerald-50/50 rounded-r">
                "{webCitationPanel.evidence_snippet}"
              </blockquote>
            </div>
          )}

          {/* All Sources list */}
          {webCitationPanel.source_urls && webCitationPanel.source_urls.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                All Sources
              </p>
              <div className="space-y-1">
                {webCitationPanel.source_urls.slice(0, 8).map((src, i) => (
                  <a
                    key={i}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-emerald-600 hover:text-emerald-800 hover:underline truncate"
                    title={src.title || src.url}
                  >
                    <span className="text-gray-400 shrink-0 w-4">{i + 1}.</span>
                    <span className="flex-1 truncate">{src.title || src.domain || src.url}</span>
                    {src.domain && <span className="text-gray-400 shrink-0 ml-1">{src.domain}</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCitationPanel = () => (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 px-4 py-3 shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <button
            onClick={() => { setRightPanelMode("transcripts"); setCitationPanel(null); setCitedSnippet(null); setCitedClaimText(''); setAudioAvailable(false); setAudioVideoId(null); setAudioDirectUrl(null); }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to transcripts
          </button>
          <button onClick={closeRightPanel} className="p-1 text-gray-400 hover:text-gray-600 shrink-0"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-3 min-w-0">
          <Thumbnail
            src={citationPanel?.thumbnail_url}
            alt=""
            className="h-10 w-10 rounded object-cover shrink-0"
          />
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">{citationPanel?.title || "Loading..."}</h3>
            {citationPanel?.podcast_name && <p className="text-xs text-gray-500 truncate">{citationPanel.podcast_name}</p>}
          </div>
        </div>
      </div>
      
      {/* Audio Player - fixed at top below header */}
      {audioAvailable && audioVideoId && (
        <div className="shrink-0 border-b border-gray-200 bg-white p-3">
          <div className="flex items-center gap-3">
            {/* Play/Pause button */}
            <button
              onClick={() => {
                if (audioRef.current) {
                  if (audioRef.current.paused) audioRef.current.play();
                  else audioRef.current.pause();
                }
              }}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              {audioPlaying ? (
                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
              ) : (
                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>
            {/* Time display */}
            <span className="text-sm text-gray-600 tabular-nums min-w-[4rem]">
              {Math.floor(audioCurrentTimeMs / 60000)}:{String(Math.floor((audioCurrentTimeMs % 60000) / 1000)).padStart(2, '0')}
              {audioDuration > 0 && <span className="text-gray-400"> / {Math.floor(audioDuration / 60000)}:{String(Math.floor((audioDuration % 60000) / 1000)).padStart(2, '0')}</span>}
            </span>
            {/* Progress bar */}
            <div 
              className="flex-1 h-2 bg-gray-200 rounded-full cursor-pointer"
              onClick={(e) => {
                if (audioRef.current && audioDuration > 0) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const pct = x / rect.width;
                  audioRef.current.currentTime = (pct * audioDuration) / 1000;
                }
              }}
            >
              <div 
                className="h-full bg-blue-600 rounded-full transition-all"
                style={{ width: audioDuration > 0 ? `${(audioCurrentTimeMs / audioDuration) * 100}%` : '0%' }}
              />
            </div>
          </div>
        </div>
      )}
      {audioLoading && (
        <div className="shrink-0 border-b border-gray-200 bg-white p-3">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
            Loading audio...
          </div>
        </div>
      )}
      
      <div ref={citationScrollRef} data-panel="citation" className="flex-1 overflow-y-auto p-4">
        {citationLoading ? (
          <div className="flex items-center justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" /></div>
        ) : citationPanel?.segments ? (
          <div className="space-y-3">
            {(() => {
              let foundFirstCited = false;
              return citationPanel.segments.map((seg, i) => {
                const isFirstCited = seg.is_cited && !foundFirstCited;
                if (isFirstCited) {
                  foundFirstCited = true;
                }
                return (
              <div key={i} data-start-ms={seg.start_ms} data-cited={seg.is_cited || undefined} ref={isFirstCited ? citedSegmentRef : null} className={`rounded-lg p-3 transition-all duration-300 ${seg.is_cited ? "border-l-4 border-blue-500 bg-blue-50/50" : "bg-gray-50"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-700">{seg.speaker}</span>
                  {audioAvailable ? (
                    <button
                      onClick={() => seekAudioToMs(seg.start_ms)}
                      className="text-xs text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                      title="Jump to this timestamp"
                    >
                      [{seg.timestamp}]
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">[{seg.timestamp}]</span>
                  )}
                </div>
                <p className="text-sm leading-relaxed">
                  {seg.is_cited && citedClaimText ? (
                    (() => {
                      // Find the best matching sentence using word overlap with LLM claim
                      const match = findBestMatchingSentence(seg.text, citedClaimText);
                      if (match) {
                        return (
                          <>
                            <span className="text-gray-500">{seg.text.slice(0, match.start)}</span>
                            <mark className="bg-blue-100 text-gray-900 rounded px-0.5 border-b-2 border-blue-400">
                              {seg.text.slice(match.start, match.end)}
                            </mark>
                            <span className="text-gray-500">{seg.text.slice(match.end)}</span>
                          </>
                        );
                      }
                      // Fallback: no match found, show full text
                      return <span className="text-gray-800">{seg.text}</span>;
                    })()
                  ) : (
                    <span className="text-gray-800">{seg.text}</span>
                  )}
                </p>
              </div>
                );
              });
            })()}
          </div>
        ) : <p className="text-sm text-gray-500 text-center py-8">No segments found</p>}
      </div>
      {citationPanel?.item_id && (
        <div className="border-t border-gray-200 p-4 shrink-0">
          <a href={`/transcript/${citationPanel.item_id}${citationPanel.cited_ms ? `?t=${citationPanel.cited_ms}` : ''}`} className="flex items-center justify-center gap-2 w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">
            <ExternalLink className="h-4 w-4" /> Open Transcript
          </a>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════ */

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  return (
    <div 
      ref={containerRef} 
      className="flex min-h-0 overflow-hidden"
      style={{ height: 'calc(100dvh - 68px)' }}
    >
      {/* Single persistent audio element - always mounted for reliable seeking */}
      {audioVideoId && (
        <audio
          ref={audioRef}
          src={audioDirectUrl || audioManager.getAudioUrl(audioVideoId)}
          preload="metadata"
          className="hidden"
          onTimeUpdate={(e) => {
            const newMs = Math.floor(e.currentTarget.currentTime * 1000);
            // Only update state if changed by 250ms+ to reduce re-renders
            setAudioCurrentTimeMs(prev => Math.abs(prev - newMs) >= 250 ? newMs : prev);
          }}
          onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration * 1000)}
          onPlay={() => setAudioPlaying(true)}
          onPause={() => setAudioPlaying(false)}
          onEnded={() => setAudioPlaying(false)}
        />
      )}
      
      {/* Mobile Sidebar Overlay */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMobileDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
        </div>
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-gray-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileDrawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ top: "68px", height: "calc(100vh - 68px)" }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Close button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-900">Library</span>
            <button onClick={() => setMobileDrawerOpen(false)} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
          {renderExpandedSidebar()}
        </div>
      </aside>

      {/* Desktop Left Sidebar */}
      <aside
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        className="hidden md:flex flex-col shrink-0 border-r border-gray-200 bg-gray-50 overflow-hidden"
        style={{ width: sidebarExpanded ? leftSidebarWidth : COLLAPSED_WIDTH }}>
        {sidebarExpanded ? renderExpandedSidebar() : renderCollapsedSidebar()}
      </aside>

      {/* Left sidebar resize handle */}
      {sidebarExpanded && (
        <div
          className="hidden md:block w-1 shrink-0 cursor-col-resize bg-gray-200 hover:bg-blue-400 active:bg-blue-500 transition-colors"
          onMouseDown={() => setIsResizingLeft(true)}
        />
      )}

      {/* Main Chat Area */}
      <main className="flex flex-1 flex-col overflow-hidden min-w-0 min-h-0">
        {/* Header - sticky so it doesn't scroll away */}
        <div className="border-b border-gray-200 bg-white px-4 py-2.5 flex items-center gap-3 shrink-0 sticky top-0 z-10">
          <button onClick={() => setMobileDrawerOpen(true)} className="md:hidden p-1.5 rounded-md text-gray-500 hover:bg-gray-100">
            <PanelLeftOpen className="w-4 h-4" />
          </button>

          <div className="flex-1 min-w-0">
            {currentSessionTitle ? (
              <div className="flex items-center gap-2">
                {editingTitle ? (
                  <input
                    type="text"
                    value={titleInput}
                    onChange={(e) => setTitleInput(e.target.value)}
                    onBlur={saveTitle}
                    onKeyDown={(e) => e.key === "Enter" && saveTitle()}
                    autoFocus
                    className="text-sm font-semibold text-gray-900 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1"
                  />
                ) : (
                  <>
                    <h1 className="text-sm font-semibold text-gray-900 truncate">{currentSessionTitle}</h1>
                    <button onClick={() => { setTitleInput(currentSessionTitle); setEditingTitle(true); }} className="p-1 text-gray-400 hover:text-gray-600" title="Edit title">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            ) : <h1 className="text-sm font-semibold text-gray-900">New Chat</h1>}
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-400">
                {singleItemContext 
                  ? (singleItemContext.title.length > 50 ? singleItemContext.title.slice(0, 50) + "..." : singleItemContext.title)
                  : hasSelection 
                    ? `${selectedFolders.size} folder${selectedFolders.size !== 1 ? "s" : ""} · ${totalEpisodes} episode${totalEpisodes !== 1 ? "s" : ""}`
                    : "Select folders to start"}
              </p>
              {singleItemContext && (
                <button
                  onClick={() => {
                    setSingleItemContext(null);
                    setSelectedItems(new Set());
                    navigate("/chat", { replace: true });
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  Clear
                </button>
              )}
              {/* Audio player for single item context - uses shared persistent audio element */}
              {singleItemContext?.audioAvailable && audioVideoId && (
                <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
                  <button
                    onClick={() => {
                      if (audioRef.current) {
                        if (audioRef.current.paused) audioRef.current.play();
                        else audioRef.current.pause();
                      }
                    }}
                    className="p-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    title="Play/Pause"
                  >
                    {audioPlaying ? (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                    ) : (
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    )}
                  </button>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {Math.floor(audioCurrentTimeMs / 60000)}:{String(Math.floor((audioCurrentTimeMs % 60000) / 1000)).padStart(2, '0')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Transcript panel toggle - pill on mobile, icon on desktop */}
          {totalEpisodes > 0 && (
            <>
              {/* Mobile: pill button */}
              <button 
                onClick={toggleTranscriptPanel} 
                className={`md:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  rightPanelMode === "transcripts" 
                    ? "bg-blue-600 text-white" 
                    : "bg-gray-800 text-white"
                }`}
              >
                <TranscriptIcon className="w-4 h-4" />
                <span>{totalEpisodes}</span>
              </button>
              {/* Desktop: icon button */}
              <button 
                onClick={toggleTranscriptPanel} 
                className={`hidden md:block p-2 rounded-lg transition-colors ${
                  rightPanelMode === "transcripts" 
                    ? "bg-blue-100 text-blue-600" 
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                }`}
                title="View transcripts"
              >
                <TranscriptIcon className="w-4 h-4" />
              </button>
            </>
          )}

          {messages.length > 0 && (
            <button onClick={copyEntireChat} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Copy entire chat">
              {copiedChat ? <CheckCheck className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 overflow-y-auto min-h-0 bg-white p-4 md:p-6 pb-24 md:pb-6"
        >
          {messages.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                  <MessageSquare className="h-7 w-7 text-gray-400" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-gray-500">{!hasSelection ? "Select folders from the sidebar to start" : "Ask questions about your transcribed content"}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-3xl mx-auto">
              {messages.map((msg, i) => (
                <div key={i} className={`group relative text-[15px] ${msg.role === "user" ? "ml-auto w-fit max-w-[80%]" : "text-gray-900 py-2"}`}>
                  {msg.role === "user" && editingIndex === i ? (
                    // Edit mode for user message
                    <div className="flex flex-col gap-2 min-w-[280px]">
                      <textarea
                        ref={(el) => {
                          if (el) {
                            el.style.height = 'auto';
                            el.style.height = el.scrollHeight + 'px';
                            el.focus();
                          }
                        }}
                        value={editText}
                        onChange={(e) => {
                          setEditText(e.target.value);
                          // Auto-resize
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            editAndResend(i);
                          }
                          if (e.key === "Escape") {
                            setEditingIndex(null);
                            setEditText("");
                          }
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[15px] text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                        style={{ minHeight: '40px', maxHeight: '200px' }}
                        rows={1}
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => { setEditingIndex(null); setEditText(""); }}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => editAndResend(i)}
                          disabled={!editText.trim()}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  ) : msg.role === "user" ? (
                    // Normal user message with edit button
                    <div className="rounded-lg p-4 bg-blue-600 text-white">
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                      <button
                        onClick={() => { setEditingIndex(i); setEditText(msg.content); }}
                        className="absolute -left-8 top-1/2 -translate-y-1/2 p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit message"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    // Assistant message
                    <>
                      <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => {
                            setArtifactContent(msg.content);
                            setArtifactTitle(extractArtifactTitle(msg.content));
                            setRightPanelMode("artifact");
                          }} 
                          className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100" 
                          title="Open as document"
                        >
                          <FileOutput className="h-4 w-4" />
                        </button>
                        <button onClick={() => copyResponse(msg.content, i)} className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Copy response">
                          {copiedIndex === i ? <CheckCheck className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                      <div className="whitespace-pre-wrap">
                        {msg.content.split("\n").map((line, li) => (
                          <span key={li}>{parseMarkdown(splitGroupedCitations(line), msg.citations, openCitation, msg.web_citations, openWebCitation)}{li < msg.content.split("\n").length - 1 && <br />}</span>
                        ))}
                      </div>
                      {/* Web Sources Legend - shows what [W1], [W2] refer to */}
                      {msg.web_citations && msg.web_citations.length > 0 && (
                        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 mb-2">
                            <span>🌐</span>
                            <span>Web Sources</span>
                          </div>
                          <div className="space-y-1">
                            {/* Sort web citations by number in ascending order */}
                            {[...msg.web_citations].sort((a, b) => (a.number || 0) - (b.number || 0)).map((wc, wi) => (
                              <a
                                key={wi}
                                href={wc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700 hover:bg-emerald-100 rounded px-2 py-1 -mx-2 transition-colors"
                              >
                                <span className="text-emerald-600 font-medium text-xs shrink-0">[W{wc.number || wi + 1}]</span>
                                <span className="truncate flex-1">{wc.title || wc.url}</span>
                                {wc.domain && (
                                  <span className="text-xs text-gray-400 shrink-0">{wc.domain}</span>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {msg.research_mode && msg.units_consumed !== undefined && msg.units_consumed > 0 && (
                        <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                          Used {msg.units_consumed} research unit{msg.units_consumed !== 1 ? 's' : ''}{msg.balance_after !== undefined && ` (${msg.balance_after} remaining)`}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {/* Research mode: Active tool calls display */}
              {loading && activeTools.length > 0 && (
                <div className="py-3 space-y-2">
                  {activeTools.map((tool, i) => (
                    <div 
                      key={`${tool.tool}-${i}`} 
                      className={`flex items-center gap-2 text-sm transition-all duration-300 ${
                        tool.status === 'running' 
                          ? 'text-gray-600' 
                          : tool.status === 'error' 
                            ? 'text-red-500' 
                            : 'text-green-600'
                      }`}
                    >
                      {tool.status === 'running' ? (
                        <>
                          <span className="text-base">{tool.icon}</span>
                          <span className="italic text-gray-500">
                            {tool.query ? `${tool.label}: "${tool.query}"` : `${tool.label}...`}
                          </span>
                          <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                        </>
                      ) : tool.status === 'done' ? (
                        <>
                          <Check className="h-4 w-4 text-green-500" />
                          <span>{tool.summary || 'Done'}</span>
                        </>
                      ) : (
                        <>
                          <X className="h-4 w-4 text-red-500" />
                          <span>{tool.summary || 'Failed'}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Standard loading indicator (when no active tools) */}
              {loading && activeTools.length === 0 && messages.length > 0 && messages[messages.length - 1].role === "assistant" && !messages[messages.length - 1].content && (
                <div className="py-2 text-gray-500">
                  <div className="flex items-center gap-2"><div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" /><span>Thinking...</span></div>
                </div>
              )}
              {loading && activeTools.length === 0 && messages.length > 0 && messages[messages.length - 1].role === "user" && (
                <div className="py-2 text-gray-500">
                  <div className="flex items-center gap-2"><div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" /><span>Thinking...</span></div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

        </div>

        {/* Input - fixed at bottom on mobile, flush with keyboard */}
        <div 
          className="px-3 pt-2 pb-1 shrink-0 bg-white border-t border-gray-200 md:relative md:pb-2 fixed bottom-0 left-0 right-0 z-30"
          style={{ paddingBottom: 'max(4px, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto max-w-3xl">
            {/* Input row */}
            <div className="flex gap-2 items-center">
              <textarea
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  // Auto-resize
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder={!hasSelection ? "Select folders first..." : "Ask about your transcripts..."}
                disabled={!hasSelection}
                rows={1}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-[15px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 resize-none overflow-hidden self-end"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
              <button onClick={sendMessage} disabled={loading || !query.trim() || !hasSelection} className="h-[44px] rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors shrink-0">Send</button>

              {/* Research Mode toggle button only */}
              {(() => {
                const isOutOfUnits = researchBalance && !researchBalance.can_research && researchBalance.balance === 0;
                
                return (
                  <button
                    onClick={() => !isOutOfUnits && setResearchMode(!researchMode)}
                    disabled={isOutOfUnits}
                    className={`flex items-center justify-center rounded-lg border h-[44px] w-[44px] transition-colors shrink-0 ${
                      isOutOfUnits
                        ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                        : researchMode
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                    title={isOutOfUnits 
                      ? "Research units exhausted" 
                      : researchMode 
                        ? "Research Mode ON: AI can search the web" 
                        : "Research Mode OFF: transcript answers only"
                    }
                  >
                    <GlobeSearchIcon 
                      size={22}
                      active={researchMode}
                      className={isOutOfUnits ? "opacity-40" : ""}
                    />
                  </button>
                );
              })()}

              {/* Speaker selector */}
              <div className="relative" ref={speakerDropdownRef}>
                <button 
                  onClick={() => setSpeakerDropdownOpen(!speakerDropdownOpen)} 
                  className={`flex items-center justify-center rounded-lg border h-[44px] w-[44px] transition-colors ${
                    selectedSpeaker
                      ? "border-purple-400 bg-purple-50"
                      : speakers.length > 0 
                        ? "border-gray-300 bg-white hover:bg-gray-50"
                        : "border-gray-200 bg-gray-50 cursor-not-allowed"
                  }`}
                  title={selectedSpeaker ? speakers.find(s => s.id === selectedSpeaker)?.canonical_name : speakers.length > 0 ? "Persona" : "No speakers available"}
                  disabled={speakers.length === 0}
                >
                  <NeuralIcon size={22} className={speakers.length === 0 ? "opacity-40" : ""} />
                </button>
                {speakerDropdownOpen && speakers.length > 0 && (
                  <div className="absolute bottom-full right-0 z-20 mb-1 w-64 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                    <button 
                      onClick={() => { setSpeakerDropdownOpen(false); navigate("/settings#speakers"); }}
                      className="w-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-purple-600 hover:bg-gray-50 text-left flex items-center gap-1"
                    >
                      Persona
                      <ExternalLink className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => { setSelectedSpeaker(null); setSpeakerDropdownOpen(false); }}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${!selectedSpeaker ? "bg-purple-50 text-purple-700" : "text-gray-700"}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold flex items-center justify-center">∞</span>
                      <span>Default (LLM)</span>
                      {!selectedSpeaker && <Check className="h-4 w-4 ml-auto" />}
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    {speakers.map((speaker) => (
                      <button
                        key={speaker.id}
                        onClick={() => { setSelectedSpeaker(speaker.id); setSpeakerDropdownOpen(false); }}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${selectedSpeaker === speaker.id ? "bg-purple-50 text-purple-700" : "text-gray-700"}`}
                      >
                        <span className="w-5 h-5 rounded-full bg-purple-200 text-purple-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                          {speaker.canonical_name?.charAt(0) || "?"}
                        </span>
                        <span className="truncate flex-1">{speaker.canonical_name}</span>
                        {selectedSpeaker === speaker.id && <Check className="h-4 w-4 shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Model selector */}
              <div className="relative" ref={modelDropdownRef}>
                <button onClick={() => setModelDropdownOpen(!modelDropdownOpen)} className={`flex items-center justify-center rounded-lg border h-[44px] w-[44px] transition-colors ${
                  modelDropdownOpen
                    ? "border-blue-400 bg-blue-50"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                }`} title={llmModels.find((m) => m.model === selectedModel)?.label}>
                  <AIChipIcon size={22} />
                </button>
                {modelDropdownOpen && (
                  <div className="absolute bottom-full right-0 z-20 mb-1 w-72 max-h-64 overflow-y-scroll rounded-lg border border-gray-200 bg-white py-1 shadow-lg" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Select Model</p>
                    {/* Group by provider */}
                    {["anthropic", "openai", "google"].map((provider, providerIdx) => {
                      const providerModels = llmModels.filter(m => m.provider === provider);
                      if (providerModels.length === 0) return null;
                      const providerLabel = provider === "anthropic" ? "Claude" : provider === "openai" ? "GPT" : "Gemini";
                      return (
                        <div key={provider}>
                          {providerIdx > 0 && <div className="border-t border-gray-100 my-1" />}
                          <p className="px-3 py-1 text-[10px] font-medium text-gray-400">{providerLabel}</p>
                          {providerModels.map((m) => (
                            <button 
                              key={m.model} 
                              onClick={() => { 
                                if (m.available) {
                                  setSelectedModel(m.model); 
                                  setModelDropdownOpen(false); 
                                }
                              }} 
                              disabled={!m.available}
                              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                                !m.available 
                                  ? "opacity-60 cursor-not-allowed" 
                                  : "hover:bg-gray-50"
                              } ${selectedModel === m.model ? "bg-blue-50 text-blue-700" : "text-gray-700"}`}
                              title={m.locked_message || undefined}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {!m.available && <Lock className="h-3 w-3 text-gray-400 shrink-0" />}
                                <span className="truncate text-sm">{m.label}</span>
                                {getModelDescription(m.model) && (
                                  <span className="text-[10px] text-gray-400 shrink-0">{getModelDescription(m.model)}</span>
                                )}
                              </div>
                              {selectedModel === m.model && m.available && <Check className="h-4 w-4 shrink-0" />}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                    {/* BYOK hint for locked models */}
                    {llmModels.some(m => !m.available) && (
                      <div className="px-3 py-2 border-t border-gray-100 mt-1">
                        <p className="text-xs text-gray-400">
                          <Lock className="h-3 w-3 inline mr-1" />
                          Add API key in <a href="/settings" className="text-blue-600 hover:underline">Settings</a>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Usage display - show chat messages and research units */}
            {chatUsage && (
              <div className="flex justify-center gap-4 text-[11px] text-gray-500 mt-1.5">
                <span>
                  💬 {chatUsage.chat.is_unlimited 
                    ? `${chatUsage.chat.used.toLocaleString()} msgs / ∞` 
                    : `${chatUsage.chat.used}/${chatUsage.chat.limit} msgs`}
                </span>
                <span>
                  🔬 {chatUsage.research.is_unlimited
                    ? `${chatUsage.research.used?.toLocaleString() || 0} used / ∞`
                    : `${chatUsage.research.used || 0} used / ${chatUsage.research.total?.toLocaleString() || 100}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Desktop Right Panel */}
      {rightPanelOpen && (
        <>
          <div className="hidden md:block w-1 shrink-0 cursor-col-resize bg-gray-200 hover:bg-blue-400 active:bg-blue-500 transition-colors" onMouseDown={() => setIsResizingRight(true)} />
          <aside className="hidden md:flex shrink-0 border-l border-gray-200 bg-white flex-col overflow-hidden" style={{ width: rightPanelWidth }}>
            {rightPanelMode === "transcripts" && renderTranscriptList()}
            {rightPanelMode === "citation" && renderCitationPanel()}
            {rightPanelMode === "web-citation" && renderWebCitationPanel()}
            {rightPanelMode === "artifact" && artifactContent && (
              <ArtifactPanel
                content={artifactContent}
                title={artifactTitle}
                onClose={() => {
                  setRightPanelMode("closed");
                  setArtifactContent(null);
                }}
                isFullscreen={artifactFullscreen}
                onToggleFullscreen={() => setArtifactFullscreen(!artifactFullscreen)}
              />
            )}
          </aside>
        </>
      )}

      {/* Fullscreen Artifact Panel (when toggled) */}
      {artifactFullscreen && artifactContent && (
        <ArtifactPanel
          content={artifactContent}
          title={artifactTitle}
          onClose={() => {
            setArtifactFullscreen(false);
            setRightPanelMode("closed");
            setArtifactContent(null);
          }}
          isFullscreen={true}
          onToggleFullscreen={() => setArtifactFullscreen(false)}
        />
      )}

      {/* Mobile Transcript Panel (Bottom Sheet) */}
      {rightPanelOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 z-40 bg-black/50 md:hidden" 
            onClick={closeRightPanel}
          />
          {/* Bottom sheet */}
          <div className="fixed inset-x-0 bottom-0 z-50 md:hidden bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col">
            {/* Drag handle */}
            <div className="flex justify-center py-2 shrink-0">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            {rightPanelMode === "transcripts" && renderTranscriptList()}
            {rightPanelMode === "citation" && renderCitationPanel()}
            {rightPanelMode === "web-citation" && renderWebCitationPanel()}
            {rightPanelMode === "artifact" && artifactContent && (
              <ArtifactPanel
                content={artifactContent}
                title={artifactTitle}
                onClose={() => {
                  setRightPanelMode("closed");
                  setArtifactContent(null);
                }}
              />
            )}
          </div>
        </>
      )}

      {/* Verification Required Modal */}
      <VerificationRequiredModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        limitType={verificationLimitType}
        currentLimit={verificationLimits?.limit || 10}
        upgradeBonus={verificationLimits?.bonus || 140}
        userEmail={undefined} // TODO: pass user email from auth context
      />
    </div>
  );
}
